import { isCapsLockOn } from "../cursor";
import { zoomAt } from "../../core/render/Viewport";
import { collectLayerTargetIds, planAlignLayers, type AlignOp } from "../../core/commands/AlignLayersCommand";
import { openWarpTextDialogForActiveLayer, getActiveTypeLayerWarp } from "./WarpTextDialog";
import { SetLayerPropertyCommand } from "../../core/commands/SetLayerPropertyCommand";

export interface ToolOptions {
  render(): HTMLElement;
  destroy(): void;
  /**
   * Re-read the tool's current settings and push them into the already-
   * rendered DOM controls, WITHOUT rebuilding the DOM or stealing focus.
   *
   * Needed because some tool settings can change without a tool-identity
   * switch (e.g. `[`/`]` resizing the brush, or opacity/flow digit
   * shortcuts) — see issue #74. `setTool()` only rebuilds the options bar
   * when the active tool changes, so those keyboard-driven mutations would
   * otherwise leave the options bar showing stale numbers. Optional because
   * most options classes have nothing that changes outside of their own
   * controls.
   */
  refresh?(): void;
}

/**
 * Photoshop's painting blend mode list used by Brush, Pencil, Color Replacement,
 * Mixer Brush, Fill, Stroke, Gradient, Paint Bucket, Smudge, Blur, Sharpen,
 * Dodge, Burn, Sponge and History Brush options bars.
 *
 * Order matches Photoshop's Mode dropdown exactly. Each entry with `value`
 * is a blend mode; entries with `value === null` are separator rows.
 *
 * Total: 29 modes + 4 separators = 33 entries.
 */
export const PAINTING_BLEND_MODES: ReadonlyArray<{ value: string | null; label: string }> = [
  { value: "normal", label: "Normal" },
  { value: "dissolve", label: "Dissolve" },
  { value: "behind", label: "Behind" },
  { value: "clear", label: "Clear" },
  { value: null, label: "—" },
  { value: "darken", label: "Darken" },
  { value: "multiply", label: "Multiply" },
  { value: "color-burn", label: "Color Burn" },
  { value: "linear-burn", label: "Linear Burn" },
  { value: "darker-color", label: "Darker Color" },
  { value: null, label: "—" },
  { value: "lighten", label: "Lighten" },
  { value: "screen", label: "Screen" },
  { value: "color-dodge", label: "Color Dodge" },
  { value: "linear-dodge", label: "Linear Dodge (Add)" },
  { value: "lighter-color", label: "Lighter Color" },
  { value: null, label: "—" },
  { value: "overlay", label: "Overlay" },
  { value: "soft-light", label: "Soft Light" },
  { value: "hard-light", label: "Hard Light" },
  { value: "vivid-light", label: "Vivid Light" },
  { value: "linear-light", label: "Linear Light" },
  { value: "pin-light", label: "Pin Light" },
  { value: "hard-mix", label: "Hard Mix" },
  { value: null, label: "—" },
  { value: "difference", label: "Difference" },
  { value: "exclusion", label: "Exclusion" },
  { value: "subtract", label: "Subtract" },
  { value: "divide", label: "Divide" },
  { value: "hue", label: "Hue" },
  { value: "saturation", label: "Saturation" },
  { value: "color", label: "Color" },
  { value: "luminosity", label: "Luminosity" },
];

/**
 * Build an HTML <option> string list for the painting blend mode dropdown.
 * Separators are rendered as disabled <option> rows with an em-dash label so
 * the visual grouping matches Photoshop.
 */
export function buildPaintingBlendModeOptions(selected: string): string {
  return PAINTING_BLEND_MODES.map((entry) => {
    if (entry.value === null) {
      return `<option disabled>──────────</option>`;
    }
    const sel = entry.value === selected ? " selected" : "";
    return `<option value="${entry.value}"${sel}>${entry.label}</option>`;
  }).join("");
}

export const TOOL_LABELS: Record<string, string> = {
  brush: "Brush",
  eraser: "Eraser",
  "marquee-rect": "Rectangular Marquee",
  "marquee-ellipse": "Elliptical Marquee",
  lasso: "Lasso",
  "lasso-poly": "Polygonal Lasso",
  "magnetic-lasso": "Magnetic Lasso",
  "quick-selection": "Quick Selection",
  "magic-wand": "Magic Wand",
  "object-selection": "Object Selection",
  crop: "Crop",
  "perspective-crop": "Perspective Crop",
  type: "Type",
  "type-vertical": "Vertical Type",
  "type-mask-horizontal": "Horizontal Type Mask",
  "type-mask-vertical": "Vertical Type Mask",
  pen: "Pen",
  "path-select": "Path Selection",
  "path-selection": "Path Selection",
  "direct-select": "Direct Selection",
  "direct-selection": "Direct Selection",
  "shape-rect": "Rectangle Shape",
  "shape-ellipse": "Ellipse Shape",
  "shape-rounded-rect": "Rounded Rectangle Shape",
  "shape-polygon": "Polygon Shape",
  "shape-line": "Line Shape",
  "shape-triangle": "Triangle Shape",
  "shape-custom": "Custom Shape",
  eyedropper: "Eyedropper",
  "color-sampler": "Color Sampler",
  ruler: "Ruler",
  note: "Note",
  count: "Count",
  move: "Move",
  hand: "Hand",
  zoom: "Zoom",
  "rotate-view": "Rotate View",
  gradient: "Gradient",
  "paint-bucket": "Paint Bucket",
  retouch: "Retouch",
  "history-brush": "History Brush",
  "art-history-brush": "Art History Brush",
  pencil: "Pencil",
  "mixer-brush": "Mixer Brush",
  "color-replacement": "Color Replacement",
  "background-eraser": "Background Eraser",
  "magic-eraser": "Magic Eraser",
  "clone-stamp": "Clone Stamp",
  "pattern-stamp": "Pattern Stamp",
  "healing-brush": "Healing Brush",
  "spot-healing": "Spot Healing Brush",
  patch: "Patch",
  "red-eye": "Red Eye",
  "content-aware-move": "Content-Aware Move",
  remove: "Remove",
  "erase-to-history": "Erase to History",
  "dodge-burn": "Dodge",
  sponge: "Sponge",
  frame: "Frame",
  slice: "Slice",
  "slice-select": "Slice Select",
  "free-transform": "Free Transform",
};

/**
 * Best-effort tool → sprite-sheet icon id map for the `.optionsbar__tool`
 * badge (see index.html's `<symbol id="i-...">` defs). Only tools with a
 * matching icon are listed; tools without an entry keep whatever icon was
 * already in the badge (see `updateToolBadge`) rather than showing a
 * missing/blank icon.
 */
export const TOOL_ICONS: Record<string, string> = {
  move: "i-move",
  "marquee-rect": "i-marquee",
  "marquee-ellipse": "i-marquee",
  lasso: "i-lasso",
  "lasso-poly": "i-lasso",
  "magnetic-lasso": "i-lasso",
  "magic-wand": "i-wand",
  "quick-selection": "i-wand",
  "object-selection": "i-wand",
  crop: "i-crop",
  "perspective-crop": "i-crop",
  eyedropper: "i-eyedropper",
  "color-sampler": "i-eyedropper",
  ruler: "i-eyedropper",
  brush: "i-brush",
  pencil: "i-brush",
  eraser: "i-eraser",
  "background-eraser": "i-eraser",
  "magic-eraser": "i-eraser",
  "erase-to-history": "i-eraser",
  "clone-stamp": "i-stamp",
  "pattern-stamp": "i-stamp",
  gradient: "i-gradient",
  retouch: "i-blur",
  "history-brush": "i-blur",
  "art-history-brush": "i-blur",
  "dodge-burn": "i-dodge",
  sponge: "i-dodge",
  pen: "i-pen",
  "path-select": "i-pen",
  "direct-select": "i-pen",
  type: "i-type",
  "type-vertical": "i-type",
  "type-mask-horizontal": "i-type",
  "type-mask-vertical": "i-type",
  "shape-rect": "i-shape",
  "shape-ellipse": "i-shape",
  "shape-rounded-rect": "i-shape",
  "shape-polygon": "i-shape",
  "shape-line": "i-shape",
  "shape-triangle": "i-shape",
  "shape-custom": "i-shape",
  hand: "i-hand",
  zoom: "i-zoom",
};

/**
 * Tool ids whose cursor should show the New/Add/Subtract/Intersect modifier
 * glyph (i163) — the same 6 selection tools that got the combine-mode button
 * strip (i60/i162).
 */
const COMBINE_GLYPH_TOOL_IDS = new Set([
  "marquee-rect", "marquee-ellipse",
  "lasso", "lasso-poly", "magnetic-lasso",
  "magic-wand", "quick-selection", "object-selection",
]);

export class ToolUX {
  private optionsBarEl: HTMLElement;
  private cursorCanvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private currentOptions: ToolOptions | null = null;
  cursorPos = { x: 0, y: 0 };
  private toolId: string | null = null;
  private currentTool: any = null;
  private scheduled = false;
  private state: any;
  // i163: live Shift/Alt state, tracked via document-level listeners so the
  // cursor overlay can draw a +/−/× combine-mode glyph while the pointer
  // hovers (no pointer-move sample is needed to know the modifier state).
  private modifierShift = false;
  private modifierAlt = false;
  private readonly handleModifierKeyEvent = (e: KeyboardEvent): void => {
    if (this.modifierShift === e.shiftKey && this.modifierAlt === e.altKey) return;
    this.modifierShift = e.shiftKey;
    this.modifierAlt = e.altKey;
    this.scheduleCursorDraw();
  };

  constructor(optionsBarEl: HTMLElement, cursorCanvas: HTMLCanvasElement, state?: any) {
    this.optionsBarEl = optionsBarEl;
    this.cursorCanvas = cursorCanvas;
    this.state = state;
    const ctx = cursorCanvas.getContext("2d");
    if (!ctx) throw new Error("Cursor canvas 2D context unavailable");
    this.ctx = ctx;
    if (typeof document !== "undefined") {
      document.addEventListener("keydown", this.handleModifierKeyEvent);
      document.addEventListener("keyup", this.handleModifierKeyEvent);
    }
  }

  setTool(toolId: string, tool: any): void {
    this.toolId = toolId;
    this.currentTool = tool;
    // #46: index.html bakes a static `.optionsbar__tool` badge (icon + tool
    // name) directly into the `.optionsbar` container. Wiping the whole
    // container's innerHTML on every tool switch (the old behavior)
    // destroyed that badge after the very first switch. Now we only ever
    // touch the badge's own label/icon, and clear a dedicated controls
    // sub-container for the per-tool option widgets.
    this.updateToolBadge(toolId);
    const controls = this.ensureControlsContainer();
    controls.innerHTML = "";
    if (this.currentOptions) this.currentOptions.destroy();
    this.currentOptions = this.buildOptions(toolId, tool);
    if (this.currentOptions) controls.appendChild(this.currentOptions.render());
    this.updateStatusBarToolName(toolId);
    this.scheduleCursorDraw();
  }

  /**
   * Force the currently-mounted options bar to re-read the active tool's
   * settings without rebuilding its DOM (issue #74). Call this after any
   * keyboard shortcut mutates tool settings directly (brush size via `[`/`]`,
   * hardness via `{`/`}`, opacity/flow digit shortcuts, etc.) so the options
   * bar doesn't show stale numbers. No-op if the current options class
   * doesn't implement `refresh()`.
   */
  refreshOptions(): void {
    this.currentOptions?.refresh?.();
  }

  /**
   * Ensure the `.optionsbar__tool` badge exists as the first child of the
   * options bar, and update its label (and icon, when a mapping is known)
   * to match the active tool. Never removed by setTool().
   */
  private updateToolBadge(toolId: string): void {
    let badge = this.optionsBarEl.querySelector<HTMLElement>(".optionsbar__tool");
    if (!badge) {
      badge = document.createElement("div");
      badge.className = "optionsbar__tool";
      badge.innerHTML = `<svg class="ico"><use href="#i-move"/></svg><span></span>`;
      this.optionsBarEl.insertBefore(badge, this.optionsBarEl.firstChild);
    }
    const label = TOOL_LABELS[toolId] ?? toolId;
    const span = badge.querySelector("span");
    if (span) span.textContent = label;
    const iconId = TOOL_ICONS[toolId];
    if (iconId) {
      const use = badge.querySelector("use");
      if (use) use.setAttribute("href", `#${iconId}`);
    }
  }

  /**
   * Return the `.optionsbar__controls` sub-container, creating it once. The
   * first time this runs it also clears any other legacy/static siblings
   * (e.g. the example controls baked into index.html next to the badge) so
   * they don't linger alongside the dynamically-built controls — but the
   * `.optionsbar__tool` badge itself is always preserved.
   */
  private ensureControlsContainer(): HTMLElement {
    let controls = this.optionsBarEl.querySelector<HTMLElement>(".optionsbar__controls");
    if (!controls) {
      const badge = this.optionsBarEl.querySelector<HTMLElement>(".optionsbar__tool");
      Array.from(this.optionsBarEl.children).forEach((child) => {
        if (child !== badge) child.remove();
      });
      controls = document.createElement("div");
      controls.className = "optionsbar__controls";
      this.optionsBarEl.appendChild(controls);
    }
    return controls;
  }

  private updateStatusBarToolName(toolId: string): void {
    const statusToolEl = document.querySelector<HTMLElement>("#status-tool");
    if (statusToolEl) {
      statusToolEl.textContent = TOOL_LABELS[toolId] ?? toolId;
    }
  }

  setCursorPos(x: number, y: number): void {
    this.cursorPos.x = x;
    this.cursorPos.y = y;
  }

  scheduleCursorDraw(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.drawCursorInternal();
    });
  }

  drawCursorInternal(): void {
    const ctx = this.ctx;
    const w = this.cursorCanvas.width;
    const h = this.cursorCanvas.height;
    ctx.clearRect(0, 0, w, h);

    const toolId = this.toolId;
    if (!toolId) return;

    // Get DPR from canvas size vs CSS size for hi-DPI displays
    const cssW = this.cursorCanvas.clientWidth || w;
    const dpr = w / cssW;
    const x = this.cursorPos.x * dpr;
    const y = this.cursorPos.y * dpr;

    switch (toolId) {
      case "brush":
      case "eraser":
      case "clone-stamp":
      case "dodge-burn":
      case "pencil": {
        // #219: Use zoom-aware radius so the circle matches the actual painted area.
        // CapsLock overrides to precise crosshair.
        // i71: Pencil keeps its brush size on `tool.size` directly rather
        // than `tool.settings.size` (see PencilToolOptions) — fall back to
        // that so Pencil gets a correctly-sized circle too.
        const s = (this.currentTool as any)?.settings;
        const size = s?.size ?? (this.currentTool as any)?.size ?? 30;
        const zoom = (this.state as any)?.viewport?.zoom ?? 1;
        // #219: CapsLock → precise crosshair
        if (isCapsLockOn()) {
          const arm = 10 * dpr;
          const gap = 2 * dpr;
          ctx.lineWidth = 1 * dpr;
          ctx.strokeStyle = "rgba(0,0,0,0.85)";
          ctx.beginPath();
          ctx.moveTo(x - arm, y); ctx.lineTo(x - gap, y);
          ctx.moveTo(x + gap, y); ctx.lineTo(x + arm, y);
          ctx.moveTo(x, y - arm); ctx.lineTo(x, y - gap);
          ctx.moveTo(x, y + gap); ctx.lineTo(x, y + arm);
          ctx.stroke();
          ctx.strokeStyle = "rgba(255,255,255,0.7)";
          ctx.beginPath();
          ctx.moveTo(x - arm + 1, y); ctx.lineTo(x - gap - 1, y);
          ctx.moveTo(x + gap + 1, y); ctx.lineTo(x + arm - 1, y);
          ctx.moveTo(x, y - arm + 1); ctx.lineTo(x, y - gap - 1);
          ctx.moveTo(x, y + gap + 1); ctx.lineTo(x, y + arm - 1);
          ctx.stroke();
          break;
        }
        const radius = Math.max(0.5, (size / 2) * zoom * dpr);
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1 * dpr;
        ctx.stroke();
        // Light second ring for visibility on dark backgrounds.
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.4)";
        if (typeof ctx.setLineDash === "function") {
          ctx.setLineDash([2 * dpr, 2 * dpr]);
        }
        ctx.stroke();
        if (typeof ctx.setLineDash === "function") {
          ctx.setLineDash([]);
        }
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.5, 1 * dpr), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.7)";
        ctx.fill();
        break;
      }
      case "marquee-rect":
      case "marquee-ellipse":
      case "lasso":
      case "lasso-poly":
      case "magnetic-lasso":
      case "object-selection":
      case "magic-wand": {
        ctx.beginPath();
        ctx.moveTo(x - 10, y); ctx.lineTo(x - 3, y);
        ctx.moveTo(x + 3, y); ctx.lineTo(x + 10, y);
        ctx.moveTo(x, y - 10); ctx.lineTo(x, y - 3);
        ctx.moveTo(x, y + 3); ctx.lineTo(x, y + 10);
        ctx.strokeStyle = "rgba(0,0,0,0.7)";
        ctx.lineWidth = 1;
        ctx.stroke();
        break;
      }
      case "move": {
        ctx.beginPath();
        ctx.moveTo(x, y - 10); ctx.lineTo(x + 6, y - 4);
        ctx.moveTo(x, y - 10); ctx.lineTo(x - 6, y - 4);
        ctx.moveTo(x, y + 10); ctx.lineTo(x + 6, y + 4);
        ctx.moveTo(x, y + 10); ctx.lineTo(x - 6, y + 4);
        ctx.moveTo(x - 10, y); ctx.lineTo(x - 4, y - 6);
        ctx.moveTo(x - 10, y); ctx.lineTo(x - 4, y + 6);
        ctx.moveTo(x + 10, y); ctx.lineTo(x + 4, y - 6);
        ctx.moveTo(x + 10, y); ctx.lineTo(x + 4, y + 6);
        ctx.moveTo(x - 4, y); ctx.lineTo(x + 4, y);
        ctx.moveTo(x, y - 4); ctx.lineTo(x, y + 4);
        ctx.strokeStyle = "rgba(0,0,0,0.7)";
        ctx.lineWidth = 1;
        ctx.stroke();
        break;
      }
      case "eyedropper": {
        ctx.beginPath();
        ctx.moveTo(x + 6, y - 8); ctx.lineTo(x + 10, y - 4);
        ctx.lineTo(x + 4, y + 2); ctx.lineTo(x, y - 2);
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.6)";
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x + 2, y); ctx.lineTo(x - 6, y + 8);
        ctx.lineTo(x - 8, y + 6); ctx.lineTo(x, y - 2);
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.4)";
        ctx.fill();
        break;
      }
      case "hand": {
        ctx.beginPath();
        ctx.arc(x - 4, y - 8, 3, 0, Math.PI * 2);
        ctx.moveTo(x - 4, y - 5); ctx.lineTo(x - 4, y + 8);
        ctx.moveTo(x - 4, y - 2); ctx.lineTo(x + 3, y - 2);
        ctx.moveTo(x - 4, y + 2); ctx.lineTo(x + 5, y + 2);
        ctx.moveTo(x - 4, y + 6); ctx.lineTo(x + 3, y + 6);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "zoom": {
        ctx.beginPath();
        ctx.arc(x - 3, y - 3, 7, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + 3, y + 3); ctx.lineTo(x + 10, y + 10);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - 7, y - 3); ctx.lineTo(x + 1, y - 3);
        ctx.moveTo(x - 3, y - 7); ctx.lineTo(x - 3, y + 1);
        ctx.strokeStyle = "rgba(0,0,0,0.7)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "type": {
        ctx.beginPath();
        ctx.moveTo(x, y - 12); ctx.lineTo(x, y + 12);
        ctx.moveTo(x - 6, y - 12); ctx.lineTo(x + 6, y - 12);
        ctx.moveTo(x - 4, y + 12); ctx.lineTo(x + 4, y + 12);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "pen": {
        ctx.beginPath();
        ctx.moveTo(x, y - 10); ctx.lineTo(x + 5, y + 2); ctx.lineTo(x - 5, y + 2);
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.6)";
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x, y + 2); ctx.lineTo(x, y + 10);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "crop": {
        const s = 10;
        // top-left corner
        ctx.beginPath();
        ctx.moveTo(x - s, y - s + 4); ctx.lineTo(x - s, y - s); ctx.lineTo(x - s + 4, y - s);
        // top-right corner
        ctx.moveTo(x + s - 4, y - s); ctx.lineTo(x + s, y - s); ctx.lineTo(x + s, y - s + 4);
        // bottom-right corner
        ctx.moveTo(x + s, y + s - 4); ctx.lineTo(x + s, y + s); ctx.lineTo(x + s - 4, y + s);
        // bottom-left corner
        ctx.moveTo(x - s + 4, y + s); ctx.lineTo(x - s, y + s); ctx.lineTo(x - s, y + s - 4);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "gradient": {
        // Gradient cursor: small crosshair with gradient arrow hint
        ctx.beginPath();
        ctx.moveTo(x - 8, y); ctx.lineTo(x + 8, y);
        ctx.moveTo(x, y - 8); ctx.lineTo(x, y + 8);
        ctx.moveTo(x + 4, y - 4); ctx.lineTo(x + 8, y); ctx.lineTo(x + 4, y + 4);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "paint-bucket": {
        // Paint bucket: tilted bucket shape
        ctx.beginPath();
        ctx.moveTo(x - 4, y - 8); ctx.lineTo(x + 6, y - 8);
        ctx.lineTo(x + 4, y + 2); ctx.lineTo(x - 6, y + 2);
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x + 6, y - 8); ctx.lineTo(x + 8, y - 12);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      case "retouch": {
        // Retouch (blur/sharpen/smudge): water drop shape
        const s = (this.currentTool as any)?.settings;
        const size = s?.size ?? 30;
        const radius = size / 2;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.lineWidth = 1;
        ctx.stroke();
        // Inner drop shape
        ctx.beginPath();
        ctx.moveTo(x, y - 4);
        ctx.quadraticCurveTo(x + 4, y, x, y + 4);
        ctx.quadraticCurveTo(x - 4, y, x, y - 4);
        ctx.fillStyle = "rgba(0,0,0,0.4)";
        ctx.fill();
        break;
      }
      case "history-brush": {
        // History brush: brush with clock/history hint
        const s = (this.currentTool as any)?.settings;
        const size = s?.size ?? 30;
        const radius = size / 2;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.lineWidth = 1;
        ctx.stroke();
        // Clock hands
        ctx.beginPath();
        ctx.moveTo(x, y); ctx.lineTo(x, y - 5);
        ctx.moveTo(x, y); ctx.lineTo(x + 3, y + 2);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      }
      // i66/i173: the rest of the brush-based retouch family, plus
      // Quick Selection (which also paints with a round brush-size hint
      // circle in Photoshop). Each tool stores its brush size under a
      // different property name (`settings.size`, `size`, or
      // `brushSize`) — try all three.
      case "pattern-stamp":
      case "healing-brush":
      case "spot-healing":
      case "red-eye":
      case "mixer-brush":
      case "color-replacement":
      case "background-eraser":
      case "art-history-brush":
      case "sponge":
      case "remove":
      case "erase-to-history":
      case "quick-selection": {
        const t = this.currentTool as any;
        const size = t?.settings?.size ?? t?.size ?? t?.brushSize ?? 30;
        const zoom = (this.state as any)?.viewport?.zoom ?? 1;
        const radius = Math.max(0.5, (size / 2) * zoom * dpr);
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1 * dpr;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.4)";
        if (typeof ctx.setLineDash === "function") {
          ctx.setLineDash([2 * dpr, 2 * dpr]);
        }
        ctx.stroke();
        if (typeof ctx.setLineDash === "function") {
          ctx.setLineDash([]);
        }
        break;
      }
    }

    // i163: draw a New/Add/Subtract/Intersect glyph next to the cursor for
    // the 6 selection tools while Shift and/or Alt is held, mirroring
    // Photoshop's cursor badges for combine-mode selection dragging.
    if (COMBINE_GLYPH_TOOL_IDS.has(toolId) && (this.modifierShift || this.modifierAlt)) {
      this.drawCombineGlyph(x, y, dpr);
    }
  }

  /** Draws the +/−/× combine-mode badge near the cursor (i163). */
  private drawCombineGlyph(x: number, y: number, dpr: number): void {
    const ctx = this.ctx;
    const glyph = this.modifierShift && this.modifierAlt ? "×" : this.modifierShift ? "+" : "−";
    const gx = x + 12 * dpr;
    const gy = y - 12 * dpr;
    const r = 7 * dpr;
    ctx.beginPath();
    ctx.arc(gx, gy, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.8)";
    ctx.lineWidth = 1 * dpr;
    ctx.stroke();
    ctx.fillStyle = "rgba(0,0,0,0.9)";
    ctx.font = `bold ${10 * dpr}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(glyph, gx, gy + 0.5 * dpr);
  }

  private buildOptions(toolId: string, tool: any): ToolOptions | null {
    switch (toolId) {
      case "brush":
        return new BrushToolOptions(tool, () => this.scheduleCursorDraw());
      case "eraser":
        return new EraserToolOptions(tool, () => this.scheduleCursorDraw());
      case "pencil":
        return new PencilToolOptions(tool, () => this.scheduleCursorDraw());
      case "marquee-rect":
      case "marquee-ellipse":
        return new MarqueeToolOptions(tool, toolId);
      case "magic-wand":
        return new MagicWandToolOptions(tool);
      case "object-selection":
        return new ObjectSelectionToolOptions(tool);
      case "clone-stamp":
        return new CloneStampToolOptions(tool, () => this.scheduleCursorDraw());
      case "pattern-stamp":
        return new PatternStampToolOptions(tool, () => this.scheduleCursorDraw());
      case "dodge-burn":
        return new DodgeBurnToolOptions(tool);
      case "sponge":
        return new SpongeToolOptions(tool, () => this.scheduleCursorDraw());
      case "crop":
        return new CropToolOptions(tool, this.state);
      case "perspective-crop":
        return new PerspectiveCropToolOptions(tool, this.state);
      case "free-transform":
        return new FreeTransformToolOptions(tool, this.state);
      case "move":
        return new MoveToolOptions(this.state);
      case "pen":
        return new PenToolOptions(tool);
      case "path-select":
      case "path-selection":
        return new PathSelectionToolOptions(tool);
      case "direct-select":
      case "direct-selection":
        return new DirectSelectionToolOptions(tool);
      case "shape-rect":
      case "shape-ellipse":
      case "shape-rounded-rect":
      case "shape-polygon":
      case "shape-triangle":
      case "shape-line":
      case "shape-custom":
        return new ShapeToolOptions(tool);
      case "gradient":
        return new GradientToolOptions(tool);
      case "paint-bucket":
        return new PaintBucketToolOptions(tool);
      case "retouch":
        return new RetouchToolOptions(tool, () => this.scheduleCursorDraw());
      case "history-brush":
        return new HistoryBrushToolOptions(tool, () => this.scheduleCursorDraw());
      case "art-history-brush":
        return new ArtHistoryBrushToolOptions(tool, () => this.scheduleCursorDraw());
      case "eyedropper":
        return new EyedropperToolOptions(tool);
      case "color-sampler":
        return new ColorSamplerToolOptions(tool);
      case "ruler":
        return new RulerToolOptions(tool);
      case "note":
        return new NoteToolOptions(tool);
      case "count":
        return new CountToolOptions(tool);
      case "type":
      case "type-mask-horizontal":
      case "type-vertical":
      case "type-mask-vertical":
        return new TypeToolOptions(tool, this.state);
      case "lasso":
      case "lasso-poly":
        return new LassoToolOptions(tool);
      case "magnetic-lasso":
        return new MagneticLassoToolOptions(tool);
      case "quick-selection":
        return new QuickSelectionToolOptions(tool);
      case "healing-brush":
        return new HealingBrushToolOptions(tool, () => this.scheduleCursorDraw());
      case "spot-healing":
        return new SpotHealingToolOptions(tool, () => this.scheduleCursorDraw());
      case "patch":
        return new PatchToolOptions(tool);
      case "red-eye":
        return new RedEyeToolOptions(tool, () => this.scheduleCursorDraw());
      case "mixer-brush":
        return new MixerBrushToolOptions(tool, () => this.scheduleCursorDraw());
      case "color-replacement":
        return new ColorReplacementToolOptions(tool, () => this.scheduleCursorDraw());
      case "background-eraser":
        return new BackgroundEraserToolOptions(tool, () => this.scheduleCursorDraw());
      case "magic-eraser":
        return new MagicEraserToolOptions(tool);
      case "content-aware-move":
        return new ContentAwareMoveToolOptions(tool);
      case "remove":
        return new RemoveToolOptions(tool, () => this.scheduleCursorDraw());
      case "erase-to-history":
        return new EraseToHistoryToolOptions(tool, () => this.scheduleCursorDraw());
      case "frame":
        return new FrameToolOptions(tool);
      case "hand":
        return new HandToolOptions(this.state);
      case "zoom":
        return new ZoomToolOptions(this.state, this.cursorCanvas);
      case "rotate-view":
        return new RotateViewToolOptions(this.state);
      case "slice":
        return new SliceToolOptions(tool);
      case "slice-select":
        return new SliceSelectToolOptions(tool);
      default:
        return new GenericToolOptions(toolId);
    }
  }
}

// ── Brush / Eraser Options ──────────────────────────────────

/**
 * Brush options bar — matches Photoshop's Brush options bar exactly.
 *
 * Widgets (in order):
 *   - Brush Preset Picker (size shown here, opens preset flyout)
 *   - Mode dropdown (29 painting blend modes — see PAINTING_BLEND_MODES)
 *   - Opacity slider + numeric input
 *   - Pen-pressure-for-opacity toggle
 *   - Flow slider + numeric input
 *   - Airbrush toggle
 *   - Smoothing slider + numeric input
 *   - Smoothing options gear (placeholder)
 *   - Pen-pressure-for-size toggle
 *   - Brush Settings panel toggle (opens F5 panel)
 *   - Painting Symmetry button
 *
 * Explicitly NOT in the options bar (these live in the F5 Brush Settings
 * panel — see issue #134):
 *   Hardness, Spacing, Size Jitter, Angle Jitter, Scatter, Opacity Jitter,
 *   Fg/Bg Jitter, Wet Edges.
 *
 * The underlying BrushTool settings still hold these properties — only the
 * options-bar UI controls are removed.
 */
/** A small set of built-in preset thumbnails for the Brush Preset Picker (i166). */
const BRUSH_PRESETS: ReadonlyArray<{ label: string; size: number; hardness: number }> = [
  { label: "Soft Round 9px", size: 9, hardness: 0 },
  { label: "Soft Round 19px", size: 19, hardness: 0 },
  { label: "Soft Round 45px", size: 45, hardness: 0 },
  { label: "Soft Round 100px", size: 100, hardness: 0 },
  { label: "Hard Round 9px", size: 9, hardness: 1 },
  { label: "Hard Round 19px", size: 19, hardness: 1 },
  { label: "Hard Round 45px", size: 45, hardness: 1 },
  { label: "Hard Round 100px", size: 100, hardness: 1 },
];

/** Symmetry axis choices for the Painting Symmetry popover (i77). */
const SYMMETRY_AXES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "none", label: "None" },
  { value: "vertical", label: "Vertical" },
  { value: "horizontal", label: "Horizontal" },
  { value: "both", label: "Both" },
];

export class BrushToolOptions implements ToolOptions {
  private tool: any;
  private onChange: () => void;
  private el: HTMLElement;
  // i77/i166/i270: dismiss any open popover on an outside click. Bound once
  // and removed in destroy() so switching away from the Brush tool doesn't
  // leak a document-level listener per activation.
  private readonly outsideClickHandler = (): void => this.closeAllPopovers(null);

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, opacity: 1, flow: 1, blendMode: "normal" };
    const blendMode = (s as any).blendMode ?? "normal";
    const opacityPct = Math.round((s.opacity ?? 1) * 100);
    const flowPct = Math.round((s.flow ?? 1) * 100);
    const smoothingPct = Math.round((s.smoothing ?? 0.1) * 100);
    const pressureForOpacity = (s as any).opacityJitterControl === "pen-pressure";
    const pressureForSize = (s as any).sizeJitterControl === "pen-pressure";
    this.el.innerHTML = `
      <span class="brush-preset-wrap" style="position:relative;display:inline-block">
        <button type="button" class="brush-preset-btn opt-btn" title="Open Brush Preset Picker">
          <span class="brush-preset-thumb"></span>
          <span class="brush-preset-size">${s.size}</span>
          <span class="brush-preset-caret">▾</span>
        </button>
        <div class="brush-preset-popover" style="display:none;position:absolute;top:100%;left:0;z-index:50;background:#3a3a3a;color:#fff;border:1px solid rgba(255,255,255,0.25);padding:6px;grid-template-columns:repeat(4,1fr);gap:4px;min-width:160px;">
          ${BRUSH_PRESETS.map((p) => `<button type="button" class="brush-preset-option opt-btn" data-size="${p.size}" data-hardness="${p.hardness}" title="${p.label}">${p.size}</button>`).join("")}
        </div>
      </span>
      <label class="opt">Mode:
        <select class="brush-blendmode-input">
          ${buildPaintingBlendModeOptions(blendMode)}
        </select>
      </label>
      <label class="opt">Opacity:
        <input type="range" min="0" max="100" value="${opacityPct}" class="brush-opacity-input" style="width:80px">
        <input type="number" min="0" max="100" value="${opacityPct}" class="brush-opacity-num" style="width:46px">%
      </label>
      <button type="button" class="brush-pressure-opacity opt-btn ${pressureForOpacity ? "is-active" : ""}" title="Always use pen pressure to control opacity" aria-pressed="${pressureForOpacity}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1"><circle cx="7" cy="7" r="5.5"/><path d="M7 1.5v5.5h5.5" stroke-linecap="round"/></svg></button>
      <label class="opt">Flow:
        <input type="range" min="0" max="100" value="${flowPct}" class="brush-flow-input" style="width:80px">
        <input type="number" min="0" max="100" value="${flowPct}" class="brush-flow-num" style="width:46px">%
      </label>
      <button type="button" class="brush-airbrush-btn opt-btn ${s.airbrush ? "is-active" : ""}" title="Enable airbrush-style buildup effects" aria-pressed="${s.airbrush ? "true" : "false"}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><path d="M5 11 Q3 9 3 7 Q3 4 5 3 L9 3 Q11 4 11 7 Q11 9 9 11Z" stroke-linejoin="round"/><line x1="7" y1="11" x2="7" y2="13"/><line x1="5" y1="7" x2="2" y2="6.5"/><line x1="9" y1="7" x2="12" y2="6.5"/></svg></button>
      <label class="opt">Smoothing:
        <input type="range" min="0" max="100" value="${smoothingPct}" class="brush-smoothing-input" style="width:80px">
        <input type="number" min="0" max="100" value="${smoothingPct}" class="brush-smoothing-num" style="width:46px">%
      </label>
      <span class="brush-smoothing-wrap" style="position:relative;display:inline-block">
        <button type="button" class="brush-smoothing-options opt-btn" title="Smoothing options"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><circle cx="7" cy="7" r="2.5"/><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.93 2.93l1.41 1.41M9.66 9.66l1.41 1.41M2.93 11.07l1.41-1.41M9.66 4.34l1.41-1.41"/></svg></button>
        <div class="brush-smoothing-popover" style="display:none;position:absolute;top:100%;left:0;z-index:50;background:#3a3a3a;color:#fff;border:1px solid rgba(255,255,255,0.25);padding:6px;min-width:180px;">
          <label class="opt" style="display:block">Method:
            <select class="brush-smoothing-mode-input">
              <option value="basic" ${((s as any).smoothingMode ?? "basic") === "basic" ? "selected" : ""}>Basic</option>
              <option value="pulled-string" ${(s as any).smoothingMode === "pulled-string" ? "selected" : ""}>Pulled String</option>
            </select>
          </label>
          <label class="opt" style="display:block"><input type="checkbox" class="brush-smoothing-catchup-input" ${(s as any).smoothingCatchUp ? "checked" : ""}> Catch-up on Stroke End</label>
          <label class="opt" style="display:block"><input type="checkbox" class="brush-smoothing-zoom-input" ${(s as any).smoothingAdjustZoom !== false ? "checked" : ""}> Adjust for Zoom</label>
        </div>
      </span>
      <button type="button" class="brush-pressure-size opt-btn ${pressureForSize ? "is-active" : ""}" title="Always use pen pressure to control size" aria-pressed="${pressureForSize}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1"><path d="M7 1.5a5.5 5.5 0 0 1 0 11Z" fill="currentColor" stroke="none"/><circle cx="7" cy="7" r="5.5"/></svg></button>
      <button type="button" class="brush-settings-toggle opt-btn" title="Toggle the Brush Settings panel (F5)"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><rect x="2" y="2" width="10" height="10" rx="1"/><line x1="2" y1="5" x2="12" y2="5"/><line x1="5" y1="2" x2="5" y2="5"/></svg></button>
      <span class="brush-symmetry-wrap" style="position:relative;display:inline-block">
        <button type="button" class="brush-symmetry-btn opt-btn ${(s as any).symmetry ? "is-active" : ""}" title="Set Painting Symmetry options" aria-pressed="${(s as any).symmetry ? "true" : "false"}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><line x1="7" y1="1" x2="7" y2="13"/><path d="M3 4L7 7L3 10"/><path d="M11 4L7 7L11 10"/></svg></button>
        <div class="brush-symmetry-popover" style="display:none;position:absolute;top:100%;left:0;z-index:50;background:#3a3a3a;color:#fff;border:1px solid rgba(255,255,255,0.25);padding:6px;min-width:120px;">
          ${SYMMETRY_AXES.map((a) => `<label class="opt" style="display:block"><input type="radio" name="brush-symmetry-axis" class="brush-symmetry-axis-input" value="${a.value}" ${((s as any).symmetryAxis ?? "none") === a.value ? "checked" : ""}> ${a.label}</label>`).join("")}
        </div>
      </span>
    `;

    this.bindSliderNumberPair(".brush-opacity-input", ".brush-opacity-num", "opacity", 0.01);
    this.bindSliderNumberPair(".brush-flow-input", ".brush-flow-num", "flow", 0.01);
    this.bindSliderNumberPair(".brush-smoothing-input", ".brush-smoothing-num", "smoothing", 0.01);

    const blendModeSelect = this.el.querySelector<HTMLSelectElement>(".brush-blendmode-input");
    blendModeSelect?.addEventListener("change", () => {
      if (this.tool?.settings) (this.tool.settings as any).blendMode = blendModeSelect!.value;
    });

    const airbrushBtn = this.el.querySelector<HTMLButtonElement>(".brush-airbrush-btn");
    airbrushBtn?.addEventListener("click", () => {
      if (!this.tool?.settings) return;
      const next = !this.tool.settings.airbrush;
      this.tool.settings.airbrush = next;
      airbrushBtn.classList.toggle("is-active", next);
      airbrushBtn.setAttribute("aria-pressed", next ? "true" : "false");
    });

    const pressureOpacityBtn = this.el.querySelector<HTMLButtonElement>(".brush-pressure-opacity");
    pressureOpacityBtn?.addEventListener("click", () => {
      if (!this.tool?.settings) return;
      const current = (this.tool.settings as any).opacityJitterControl === "pen-pressure";
      const next = !current;
      (this.tool.settings as any).opacityJitterControl = next ? "pen-pressure" : "off";
      pressureOpacityBtn.classList.toggle("is-active", next);
      pressureOpacityBtn.setAttribute("aria-pressed", next ? "true" : "false");
    });

    const pressureSizeBtn = this.el.querySelector<HTMLButtonElement>(".brush-pressure-size");
    pressureSizeBtn?.addEventListener("click", () => {
      if (!this.tool?.settings) return;
      const current = (this.tool.settings as any).sizeJitterControl === "pen-pressure";
      const next = !current;
      (this.tool.settings as any).sizeJitterControl = next ? "pen-pressure" : "off";
      pressureSizeBtn.classList.toggle("is-active", next);
      pressureSizeBtn.setAttribute("aria-pressed", next ? "true" : "false");
    });

    const settingsBtn = this.el.querySelector<HTMLButtonElement>(".brush-settings-toggle");
    settingsBtn?.addEventListener("click", () => {
      // Dispatch a synthetic F5 keypress so existing global handlers can open
      // the Brush Settings panel without ToolUX importing main.ts directly.
      const evt = new KeyboardEvent("keydown", { key: "F5", code: "F5", bubbles: true });
      document.dispatchEvent(evt);
    });

    // i77/i270: real Painting Symmetry popover — an axis picker (None /
    // Vertical / Horizontal / Both) instead of a bare on/off toggle.
    // `settings.symmetryAxis` is a new dynamic field (not yet consumed by
    // BrushTool's stamp-placement path — mirroring stamps across the chosen
    // axis is a follow-up requiring BrushTool.ts, out of scope here).
    // `settings.symmetry` is kept in sync (true whenever axis !== "none") so
    // any future/other consumer reading the plain boolean keeps working.
    const symmetryBtn = this.el.querySelector<HTMLButtonElement>(".brush-symmetry-btn");
    const symmetryPopover = this.el.querySelector<HTMLElement>(".brush-symmetry-popover");
    symmetryBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.togglePopover(symmetryPopover, symmetryBtn, "block");
    });
    this.el.querySelectorAll<HTMLInputElement>(".brush-symmetry-axis-input").forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked || !this.tool?.settings) return;
        const axis = radio.value;
        (this.tool.settings as any).symmetryAxis = axis;
        (this.tool.settings as any).symmetry = axis !== "none";
        symmetryBtn?.classList.toggle("is-active", axis !== "none");
        symmetryBtn?.setAttribute("aria-pressed", axis !== "none" ? "true" : "false");
      });
    });

    // i166/i270: real Brush Preset Picker — a grid of preset thumbnails.
    // Clicking one applies `settings.size`/`settings.hardness`, both of
    // which BrushTool already reads while painting (unlike Smoothing/
    // Symmetry, this has a real, immediate painting effect).
    const presetBtn = this.el.querySelector<HTMLButtonElement>(".brush-preset-btn");
    const presetPopover = this.el.querySelector<HTMLElement>(".brush-preset-popover");
    presetBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.togglePopover(presetPopover, presetBtn, "grid");
    });
    this.el.querySelectorAll<HTMLButtonElement>(".brush-preset-option").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!this.tool?.settings) return;
        const size = parseInt(btn.dataset["size"] ?? "30") || 30;
        const hardness = parseFloat(btn.dataset["hardness"] ?? "0.5");
        this.tool.settings.size = size;
        this.tool.settings.hardness = hardness;
        const sizeLabel = this.el.querySelector<HTMLSpanElement>(".brush-preset-size");
        if (sizeLabel) sizeLabel.textContent = String(size);
        if (presetPopover) presetPopover.style.display = "none";
        this.onChange();
      });
    });

    // i270: real Smoothing options popover (Method / Catch-up / Adjust for
    // Zoom). These are new dynamic `settings.*` fields — BrushTool's stroke
    // smoothing today only reads `settings.smoothing` (the slider above);
    // consuming the extra fields is a follow-up (out of scope here).
    const smoothingOptionsBtn = this.el.querySelector<HTMLButtonElement>(".brush-smoothing-options");
    const smoothingPopover = this.el.querySelector<HTMLElement>(".brush-smoothing-popover");
    smoothingOptionsBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.togglePopover(smoothingPopover, smoothingOptionsBtn, "block");
    });
    const smoothingModeInput = this.el.querySelector<HTMLSelectElement>(".brush-smoothing-mode-input");
    smoothingModeInput?.addEventListener("change", () => {
      if (this.tool?.settings) (this.tool.settings as any).smoothingMode = smoothingModeInput.value;
    });
    const smoothingCatchupInput = this.el.querySelector<HTMLInputElement>(".brush-smoothing-catchup-input");
    smoothingCatchupInput?.addEventListener("change", () => {
      if (this.tool?.settings) (this.tool.settings as any).smoothingCatchUp = smoothingCatchupInput.checked;
    });
    const smoothingZoomInput = this.el.querySelector<HTMLInputElement>(".brush-smoothing-zoom-input");
    smoothingZoomInput?.addEventListener("change", () => {
      if (this.tool?.settings) (this.tool.settings as any).smoothingAdjustZoom = smoothingZoomInput.checked;
    });

    // Close any open popover on an outside click.
    document.addEventListener("click", this.outsideClickHandler);
  }

  /** Hide every popover in this options bar except `keep` (i77/i166/i270). */
  private closeAllPopovers(keep: HTMLElement | null): void {
    this.el.querySelectorAll<HTMLElement>(".brush-preset-popover, .brush-smoothing-popover, .brush-symmetry-popover").forEach((p) => {
      if (p !== keep) p.style.display = "none";
    });
  }

  /**
   * Toggle a brush-options popover open/closed (F9). The options bar is a
   * fixed 26px-tall strip with `overflow-y:hidden`, so a popover anchored
   * inside it with `position:absolute;top:100%` is clipped away — invisible
   * and unusable. On open, promote the popover to `position:fixed`, anchor it
   * directly under its trigger button, and clamp it into the viewport (flip
   * above the button when there is no room below), mirroring the shared
   * menu/HUD clamps (F6/F7). Closing just hides it.
   */
  private togglePopover(popover: HTMLElement | null, btn: HTMLElement | null, openDisplay: string): void {
    if (!popover) return;
    const wasOpen = popover.style.display !== "none" && popover.style.display !== "";
    this.closeAllPopovers(popover);
    if (wasOpen) {
      popover.style.display = "none";
      return;
    }
    // Escape the clipping options bar, then measure and clamp.
    const PAD = 4;
    popover.style.position = "fixed";
    popover.style.display = openDisplay;
    const br = btn?.getBoundingClientRect();
    const pr = popover.getBoundingClientRect();
    let left = br ? br.left : PAD;
    let top = br ? br.bottom : PAD;
    if (left + pr.width > window.innerWidth - PAD) left = window.innerWidth - pr.width - PAD;
    if (left < PAD) left = PAD;
    if (top + pr.height > window.innerHeight - PAD) {
      const above = (br ? br.top : top) - pr.height;
      top = above >= PAD ? above : Math.max(PAD, window.innerHeight - pr.height - PAD);
    }
    popover.style.left = `${Math.round(left)}px`;
    popover.style.top = `${Math.round(top)}px`;
  }

  /**
   * Bidirectionally bind a range slider and a numeric input to the same
   * underlying brush settings value. Both inputs stay in sync with one
   * another, and changes always trigger the cursor-redraw callback.
   */
  private bindSliderNumberPair(rangeSel: string, numSel: string, key: string, scale: number): void {
    const range = this.el.querySelector<HTMLInputElement>(rangeSel);
    const num = this.el.querySelector<HTMLInputElement>(numSel);
    if (!range || !num) return;
    const apply = (raw: string): void => {
      const n = Math.max(0, Math.min(100, parseFloat(raw) || 0));
      range.value = String(n);
      num.value = String(n);
      if (this.tool?.settings) this.tool.settings[key] = n * scale;
      this.onChange();
    };
    range.addEventListener("input", () => apply(range.value));
    num.addEventListener("input", () => apply(num.value));
    num.addEventListener("change", () => apply(num.value));
  }

  render(): HTMLElement {
    return this.el;
  }

  /**
   * Re-read `tool.settings` and push the values into the existing controls
   * without rebuilding the DOM (issue #74). Needed because `[`/`]` (size),
   * `{`/`}` (hardness — not shown here but kept in sync for parity) and the
   * opacity/flow digit shortcuts mutate `tool.settings` directly from
   * main.ts's keydown handler, bypassing this options bar entirely.
   */
  refresh(): void {
    const s = this.tool?.settings;
    if (!s) return;
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".brush-preset-size");
    if (sizeLabel) sizeLabel.textContent = String(s.size);
    this.setPair(".brush-opacity-input", ".brush-opacity-num", Math.round((s.opacity ?? 1) * 100));
    this.setPair(".brush-flow-input", ".brush-flow-num", Math.round((s.flow ?? 1) * 100));
    this.setPair(".brush-smoothing-input", ".brush-smoothing-num", Math.round((s.smoothing ?? 0.1) * 100));
    const blendModeSelect = this.el.querySelector<HTMLSelectElement>(".brush-blendmode-input");
    if (blendModeSelect) blendModeSelect.value = (s as any).blendMode ?? "normal";
    const airbrushBtn = this.el.querySelector<HTMLButtonElement>(".brush-airbrush-btn");
    if (airbrushBtn) {
      airbrushBtn.classList.toggle("is-active", !!s.airbrush);
      airbrushBtn.setAttribute("aria-pressed", s.airbrush ? "true" : "false");
    }
    const pressureForOpacity = (s as any).opacityJitterControl === "pen-pressure";
    const pressureOpacityBtn = this.el.querySelector<HTMLButtonElement>(".brush-pressure-opacity");
    if (pressureOpacityBtn) {
      pressureOpacityBtn.classList.toggle("is-active", pressureForOpacity);
      pressureOpacityBtn.setAttribute("aria-pressed", pressureForOpacity ? "true" : "false");
    }
    const pressureForSize = (s as any).sizeJitterControl === "pen-pressure";
    const pressureSizeBtn = this.el.querySelector<HTMLButtonElement>(".brush-pressure-size");
    if (pressureSizeBtn) {
      pressureSizeBtn.classList.toggle("is-active", pressureForSize);
      pressureSizeBtn.setAttribute("aria-pressed", pressureForSize ? "true" : "false");
    }
  }

  private setPair(rangeSel: string, numSel: string, value: number): void {
    const range = this.el.querySelector<HTMLInputElement>(rangeSel);
    const num = this.el.querySelector<HTMLInputElement>(numSel);
    if (range) range.value = String(value);
    if (num) num.value = String(value);
  }

  destroy(): void {
    document.removeEventListener("click", this.outsideClickHandler);
  }
}

// ── Shared selection-tool combine-mode strip + Select-and-Mask button ──────
//
// Photoshop's New Selection / Add to Selection / Subtract from Selection /
// Intersect with Selection icon buttons, shared by every selection tool's
// options bar (Marquee ×2, Lasso ×3, Magic Wand, Quick Selection, Object
// Selection — i60/i162). Persists the user's explicit click on
// `tool.combineMode` so a future change to each tool's onPointerDown can read
// it as an override to the shift/alt-derived pickCombine() default (tracked
// as a follow-up — the *Tool.ts pickCombine() helpers don't consume this
// field yet, same "dynamic UI-only field" convention used elsewhere in this
// file).
const COMBINE_MODE_BUTTONS: ReadonlyArray<{ mode: string; title: string; label: string }> = [
  { mode: "new", title: "New Selection", label: "▭" },
  { mode: "add", title: "Add to Selection", label: "▭₊" },
  { mode: "subtract", title: "Subtract from Selection", label: "▭₋" },
  { mode: "intersect", title: "Intersect with Selection", label: "▭ₓ" },
];

function buildCombineModeButtonsHtml(current: string): string {
  const buttons = COMBINE_MODE_BUTTONS.map(
    (b) =>
      `<button type="button" class="combine-mode-btn opt-btn ${b.mode === current ? "is-active" : ""}" data-combine="${b.mode}" title="${b.title}" aria-pressed="${b.mode === current}">${b.label}</button>`,
  ).join("");
  return `<span class="optionsbar__combine-group" style="display:inline-flex;gap:2px;margin-right:8px;">${buttons}</span>`;
}

function wireCombineModeButtons(el: HTMLElement, tool: any): void {
  const buttons = el.querySelectorAll<HTMLButtonElement>(".combine-mode-btn");
  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const mode = btn.dataset["combine"] ?? "new";
      if (tool) tool.combineMode = mode;
      buttons.forEach((b) => {
        const active = b.dataset["combine"] === mode;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", active ? "true" : "false");
      });
    });
  });
}

/**
 * "Select and Mask…" button markup, shared by the same 6 selection tools.
 * Dispatches a bubbling custom event rather than calling the Select menu's
 * handler directly — main.ts owns `filterDialog.open("Select and Mask", …)`
 * (out of scope here) and needs to add a listener for this event (or expose
 * a standalone function both call), same placeholder-event convention as
 * BrushToolOptions's `.brush-preset-btn` (pixelpa:open-brush-presets).
 */
function buildSelectAndMaskButtonHtml(): string {
  return `<button type="button" class="select-and-mask-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Select and Mask…</button>`;
}

function wireSelectAndMaskButton(el: HTMLElement): void {
  const btn = el.querySelector<HTMLButtonElement>(".select-and-mask-btn");
  btn?.addEventListener("click", () => {
    const evt = new CustomEvent("pixelpa:open-select-and-mask", { bubbles: true });
    el.dispatchEvent(evt);
  });
}

/**
 * Build a minimal object satisfying core/tools/Tool.ToolContext from an
 * EditorState instance (i107). ToolContext is a structural interface, so
 * this doesn't require importing/touching Tool.ts or EditorState.ts — it
 * only reads/calls members EditorState already exposes publicly. Used to let
 * options-bar numeric fields (e.g. Free Transform's X/Y/W%/H%/Rotation/Skew)
 * re-emit a transient preview through the same ctx.emitTransient() pipeline
 * pointer-drag already uses, without EditorState needing a dedicated
 * "emit a transform preview" API of its own.
 */
function buildTransientCtxFromState(state: any): any {
  if (!state) return null;
  return {
    doc: state.doc,
    viewport: state.viewport,
    fgColor: state.fgColor,
    bgColor: state.bgColor,
    quickMask: state.quickMask,
    autoSelect: state.autoSelect,
    selectedLayerIds: state.selectedLayerIds ?? new Set(),
    emit: (cmd: any) => state.execute(cmd),
    emitTransient: (cmd: any) => state.applyTransient(cmd),
    setViewport: (patch: any) => state.setViewport?.(patch),
    setForegroundColor: (c: any) => state.setForegroundColor?.(c),
    setBackgroundColor: (c: any) => state.setBackgroundColor?.(c),
    requestRender: () => state.requestRender?.(),
    showTip: () => {},
    selectLayer: (id: string) => { state.setActiveLayer?.(id); },
  };
}

// ── Marquee Tool Options ────────────────────────────────────

export class MarqueeToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private styleSelect: HTMLSelectElement | null = null;
  private widthInput: HTMLInputElement | null = null;
  private heightInput: HTMLInputElement | null = null;
  private fixedInputsRow: HTMLElement | null = null;

  constructor(tool: any, toolId?: string) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const feather = tool?.feather ?? 0;
    const antialias = tool?.antiAlias !== false;
    const style = tool?.style ?? "normal";
    const fw = tool?.fixedWidth ?? 100;
    const fh = tool?.fixedHeight ?? 100;
    // i156: Photoshop's Rectangular Marquee has no Anti-alias checkbox at all
    // (rectangular selections are always hard-edged) — only Elliptical
    // Marquee shows it, defaulting ON.
    const showAntiAlias = toolId !== "marquee-rect";
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Style:
        <select class="marquee-style-input">
          <option value="normal" ${style === "normal" ? "selected" : ""}>Normal</option>
          <option value="fixed-ratio" ${style === "fixed-ratio" ? "selected" : ""}>Fixed Ratio</option>
          <option value="fixed-size" ${style === "fixed-size" ? "selected" : ""}>Fixed Size</option>
        </select>
      </label>
      <span class="marquee-fixed-inputs" style="display:${style === "normal" ? "none" : "inline"}">
        <label class="opt">W: <input type="number" min="1" max="10000" value="${fw}" class="marquee-fixed-w-input" style="width:60px"></label>
        <label class="opt">H: <input type="number" min="1" max="10000" value="${fh}" class="marquee-fixed-h-input" style="width:60px"></label>
      </span>
      <label class="opt">Feather: <input type="number" min="0" max="1000" value="${feather}" class="marquee-feather-input" style="width:50px"></label>
      ${showAntiAlias ? `<label class="opt"><input type="checkbox" ${antialias ? "checked" : ""} class="marquee-antialias-input"> Anti-alias</label>` : ""}
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    this.styleSelect = this.el.querySelector<HTMLSelectElement>(".marquee-style-input");
    this.fixedInputsRow = this.el.querySelector<HTMLElement>(".marquee-fixed-inputs");
    this.widthInput = this.el.querySelector<HTMLInputElement>(".marquee-fixed-w-input");
    this.heightInput = this.el.querySelector<HTMLInputElement>(".marquee-fixed-h-input");
    const featherInput = this.el.querySelector<HTMLInputElement>(".marquee-feather-input");
    const aaInput = this.el.querySelector<HTMLInputElement>(".marquee-antialias-input");

    this.styleSelect?.addEventListener("change", () => {
      if (!this.tool) return;
      const v = this.styleSelect!.value as "normal" | "fixed-ratio" | "fixed-size";
      this.tool.style = v;
      if (this.fixedInputsRow) {
        this.fixedInputsRow.style.display = v === "normal" ? "none" : "inline";
      }
    });

    this.widthInput?.addEventListener("change", () => {
      if (this.tool) this.tool.fixedWidth = Math.max(1, parseInt(this.widthInput!.value) || 1);
    });
    this.heightInput?.addEventListener("change", () => {
      if (this.tool) this.tool.fixedHeight = Math.max(1, parseInt(this.heightInput!.value) || 1);
    });

    featherInput?.addEventListener("change", () => {
      if (this.tool) this.tool.feather = Math.max(0, parseFloat(featherInput.value) || 0);
    });
    aaInput?.addEventListener("change", () => {
      if (this.tool) this.tool.antiAlias = aaInput.checked;
    });
  }

  render(): HTMLElement {
    return this.el;
  }

  destroy(): void {
    this.styleSelect = null;
    this.widthInput = null;
    this.heightInput = null;
    this.fixedInputsRow = null;
  }
}

// ── Generic Tool Options (fallback) ─────────────────────────

export class GenericToolOptions implements ToolOptions {
  private el: HTMLElement;

  constructor(toolId: string) {
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    this.el.textContent = TOOL_LABELS[toolId] ?? toolId;
  }

  render(): HTMLElement {
    return this.el;
  }

  destroy(): void {
    // no cleanup needed
  }
}

// ── Magic Wand Tool Options ─────────────────────────────────

export class MagicWandToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const tol = tool?.tolerance ?? 32;
    const contiguous = tool?.contiguous !== false;
    const aa = tool?.antiAlias !== false;
    const sampleSize = tool?.sampleSize ?? 1;
    const sampleAllLayers = tool?.sampleAllLayers === true;
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${tol}" class="wand-tolerance-input" style="width:50px"></label>
      <label class="opt"><input type="checkbox" ${aa ? "checked" : ""} class="wand-antialias-input"> Anti-alias</label>
      <label class="opt"><input type="checkbox" ${contiguous ? "checked" : ""} class="wand-contiguous-input"> Contiguous</label>
      <label class="opt">Sample Size:
        <select class="wand-samplesize-input">
          <option value="1" ${sampleSize === 1 ? "selected" : ""}>Point Sample</option>
          <option value="3" ${sampleSize === 3 ? "selected" : ""}>3×3 Average</option>
          <option value="5" ${sampleSize === 5 ? "selected" : ""}>5×5 Average</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="wand-samplealllayers-input"> Sample All Layers</label>
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    const tolInput = this.el.querySelector<HTMLInputElement>(".wand-tolerance-input");
    const aaInput = this.el.querySelector<HTMLInputElement>(".wand-antialias-input");
    const contInput = this.el.querySelector<HTMLInputElement>(".wand-contiguous-input");
    const sampleSizeInput = this.el.querySelector<HTMLSelectElement>(".wand-samplesize-input");
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".wand-samplealllayers-input");

    tolInput?.addEventListener("change", () => {
      if (this.tool) this.tool.tolerance = Math.max(0, Math.min(255, parseInt(tolInput.value) || 0));
    });
    aaInput?.addEventListener("change", () => {
      if (this.tool) this.tool.antiAlias = aaInput.checked;
    });
    contInput?.addEventListener("change", () => {
      if (this.tool) this.tool.contiguous = contInput.checked;
    });
    sampleSizeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleSize = parseInt(sampleSizeInput.value) as 1 | 3 | 5;
    });
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Clone Stamp Tool Options ────────────────────────────────

export class CloneStampToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 1 };
    const aligned = tool?.aligned !== false;
    const blendMode = tool?.blendMode ?? "normal";
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="stamp-mode-input">
          <option value="normal" ${blendMode === "normal" ? "selected" : ""}>Normal</option>
          <option value="dissolve" ${blendMode === "dissolve" ? "selected" : ""}>Dissolve</option>
          <option value="behind" ${blendMode === "behind" ? "selected" : ""}>Behind</option>
          <option value="clear" ${blendMode === "clear" ? "selected" : ""}>Clear</option>
        </select>
      </label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="stamp-size-input"> <span class="stamp-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="stamp-hardness-input"> <span class="stamp-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 1) * 100)}" class="stamp-opacity-input"> <span class="stamp-opacity-label">${Math.round((s.opacity ?? 1) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 1) * 100)}" class="stamp-flow-input"> <span class="stamp-flow-label">${Math.round((s.flow ?? 1) * 100)}%</span></label>
      <label class="opt"><input type="checkbox" ${aligned ? "checked" : ""} class="stamp-aligned-input"> Aligned</label>
      <label class="opt">Sample:
        <select class="stamp-sample-input">
          <option value="current">Current Layer</option>
          <option value="below">Current & Below</option>
          <option value="all">All Layers</option>
        </select>
      </label>
    `;
    this.bindSlider(".stamp-size-input", ".stamp-size-label", "size", 1, false);
    this.bindSlider(".stamp-hardness-input", ".stamp-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".stamp-opacity-input", ".stamp-opacity-label", "opacity", 0.01, true);
    this.bindSlider(".stamp-flow-input", ".stamp-flow-label", "flow", 0.01, true);
    // #81: this select previously had no change listener at all, so picking
    // a blend mode silently did nothing. CloneStampTool now has a real
    // `blendMode` field to write into (note: the raster compositor does not
    // yet read it during the actual clone stamp, same gap as Brush's Mode
    // dropdown — see PAINTING_BLEND_MODES doc comment above).
    const stampModeInput = this.el.querySelector<HTMLSelectElement>(".stamp-mode-input");
    stampModeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.blendMode = stampModeInput.value;
    });
    const alignedInput = this.el.querySelector<HTMLInputElement>(".stamp-aligned-input");
    alignedInput?.addEventListener("change", () => {
      if (this.tool) this.tool.aligned = alignedInput.checked;
    });
    const sampleInput = this.el.querySelector<HTMLSelectElement>(".stamp-sample-input");
    sampleInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleMode = sampleInput.value as "current" | "below" | "all";
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) {
        this.tool.settings[key] = parseFloat(input.value) * scale;
      }
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Dodge / Burn / Sponge Tool Options ──────────────────────

export class DodgeBurnToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const mode = tool?.mode ?? "dodge";
    const range = tool?.range ?? "midtones";
    const exposure = tool?.exposure ?? 50;
    const saturate = tool?.saturate !== false;
    // i178: `protectTones` already exists on DodgeBurnTool (defaults true) and
    // is consumed to dampen dodge highlights / burn shadows — only the
    // options-bar checkbox for it was missing.
    const protectTones = tool?.protectTones !== false;
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="db-mode-input">
          <option value="dodge" ${mode === "dodge" ? "selected" : ""}>Dodge</option>
          <option value="burn" ${mode === "burn" ? "selected" : ""}>Burn</option>
          <option value="sponge" ${mode === "sponge" ? "selected" : ""}>Sponge</option>
        </select>
      </label>
      <label class="opt">Range:
        <select class="db-range-input">
          <option value="shadows" ${range === "shadows" ? "selected" : ""}>Shadows</option>
          <option value="midtones" ${range === "midtones" ? "selected" : ""}>Midtones</option>
          <option value="highlights" ${range === "highlights" ? "selected" : ""}>Highlights</option>
        </select>
      </label>
      <label class="opt">Exposure: <input type="range" min="0" max="100" value="${exposure}" class="db-exposure-input"> <span class="db-exposure-label">${exposure}%</span></label>
      <label class="opt"><input type="checkbox" ${saturate ? "checked" : ""} class="db-saturate-input"> Saturate (Sponge)</label>
      <label class="opt"><input type="checkbox" ${protectTones ? "checked" : ""} class="db-protecttones-input"> Protect Tones</label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${tool?.settings?.size ?? 30}" class="db-size-input"> <span class="db-size-label">${tool?.settings?.size ?? 30}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((tool?.settings?.hardness ?? 0.5) * 100)}" class="db-hardness-input"> <span class="db-hardness-label">${Math.round((tool?.settings?.hardness ?? 0.5) * 100)}%</span></label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".db-mode-input");
    const rangeInput = this.el.querySelector<HTMLSelectElement>(".db-range-input");
    const exposureInput = this.el.querySelector<HTMLInputElement>(".db-exposure-input");
    const saturateInput = this.el.querySelector<HTMLInputElement>(".db-saturate-input");
    const protectTonesInput = this.el.querySelector<HTMLInputElement>(".db-protecttones-input");
    const sizeInput = this.el.querySelector<HTMLInputElement>(".db-size-input");
    const hardnessInput = this.el.querySelector<HTMLInputElement>(".db-hardness-input");
    const exposureLabel = this.el.querySelector<HTMLSpanElement>(".db-exposure-label");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".db-size-label");
    const hardnessLabel = this.el.querySelector<HTMLSpanElement>(".db-hardness-label");

    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value as any;
    });
    rangeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.range = rangeInput.value as any;
    });
    exposureInput?.addEventListener("input", () => {
      if (this.tool) this.tool.exposure = parseInt(exposureInput.value) || 0;
      if (exposureLabel) exposureLabel.textContent = `${exposureInput.value}%`;
    });
    saturateInput?.addEventListener("change", () => {
      if (this.tool) this.tool.saturate = saturateInput.checked;
    });
    protectTonesInput?.addEventListener("change", () => {
      if (this.tool) this.tool.protectTones = protectTonesInput.checked;
    });
    sizeInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
    });
    hardnessInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.hardness = (parseInt(hardnessInput.value) || 0) / 100;
      if (hardnessLabel) hardnessLabel.textContent = `${hardnessInput.value}%`;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Crop Tool Options ───────────────────────────────────────

export class CropToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private state: any;

  constructor(tool: any, state?: any) {
    this.tool = tool;
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const deletePixels = tool?.deletePixels === true;
    // i108: these already exist as real, consumed fields on CropTool — only
    // the options-bar controls for them were missing.
    const shieldOutside = tool?.shieldOutside !== false;
    const shieldOpacityPct = Math.round((tool?.shieldOpacity ?? 0.75) * 100);
    const resolution = tool?.resolution ?? 72;
    // i105: contentAware already exists as a real field on CropTool — only
    // the options-bar checkbox for it was missing.
    const contentAware = tool?.contentAware === true;
    this.el.innerHTML = `
      <label class="opt">Crop Preset:
        <select class="crop-preset-input">
          <option value="unconstrained">Unconstrained</option>
          <option value="original">Original Ratio</option>
          <option value="1:1">1:1 (Square)</option>
          <option value="4:5">4:5</option>
          <option value="4:3">4:3</option>
          <option value="16:9">16:9</option>
          <option value="16:10">16:10</option>
          <option value="2:3">2:3</option>
          <option value="3:2">3:2</option>
        </select>
      </label>
      <label class="opt">W: <input type="number" min="0" step="any" placeholder="e.g. 4" class="crop-w-input" style="width:50px"></label>
      <button type="button" class="crop-swap-btn opt-btn" title="Swap width and height" style="padding:2px 6px;cursor:pointer;">⇄</button>
      <label class="opt">H: <input type="number" min="0" step="any" placeholder="e.g. 3" class="crop-h-input" style="width:50px"></label>
      <label class="opt">Resolution: <input type="number" min="1" max="2400" value="${resolution}" class="crop-resolution-input" style="width:56px"> px/in</label>
      <label class="opt">Overlay:
        <select class="crop-overlay-input">
          <option value="rule-thirds">Rule of Thirds</option>
          <option value="grid">Grid</option>
          <option value="diagonal">Diagonal</option>
          <option value="golden-ratio">Golden Ratio</option>
          <option value="golden-spiral">Golden Spiral</option>
          <option value="triangle">Triangle</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" ${deletePixels ? "checked" : ""} class="crop-delete-input"> Delete Cropped Pixels</label>
      <label class="opt"><input type="checkbox" ${contentAware ? "checked" : ""} class="crop-contentaware-input"> Content-Aware</label>
      <label class="opt"><input type="checkbox" ${shieldOutside ? "checked" : ""} class="crop-shield-input"> Shield</label>
      <label class="opt">Shield Opacity: <input type="range" min="0" max="100" value="${shieldOpacityPct}" class="crop-shieldopacity-input" ${shieldOutside ? "" : "disabled"}> <span class="crop-shieldopacity-label">${shieldOpacityPct}%</span></label>
      <button class="crop-reset-btn" type="button" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Reset</button>
      <button class="crop-straighten-btn" type="button" style="margin-left:4px;padding:2px 10px;cursor:pointer;">Straighten</button>
      <button class="crop-commit-btn" type="button" title="Commit crop (Enter)" style="margin-left:12px;padding:2px 10px;cursor:pointer;font-weight:bold;">&#10003;</button>
      <button class="crop-cancel-btn" type="button" title="Cancel crop (Esc)" style="margin-left:2px;padding:2px 10px;cursor:pointer;">&#10005;</button>
    `;
    const presetInput = this.el.querySelector<HTMLSelectElement>(".crop-preset-input");
    const wInput = this.el.querySelector<HTMLInputElement>(".crop-w-input");
    const hInput = this.el.querySelector<HTMLInputElement>(".crop-h-input");
    const swapBtn = this.el.querySelector<HTMLButtonElement>(".crop-swap-btn");
    const RATIOS: Record<string, number | null> = {
      "unconstrained": null,
      "1:1": 1,
      "4:5": 4 / 5,
      "4:3": 4 / 3,
      "16:9": 16 / 9,
      "16:10": 16 / 10,
      "2:3": 2 / 3,
      "3:2": 3 / 2,
    };
    presetInput?.addEventListener("change", () => {
      if (!this.tool) return;
      const v = presetInput.value;
      if (v === "original") {
        const doc = this.state?.doc;
        const ratio = doc && doc.height > 0 ? doc.width / doc.height : null;
        this.tool.aspectRatio = ratio;
        if (wInput && hInput && doc) { wInput.value = String(doc.width); hInput.value = String(doc.height); }
        return;
      }
      this.tool.aspectRatio = RATIOS[v] ?? null;
      if (wInput) wInput.value = "";
      if (hInput) hInput.value = "";
    });
    // i108: explicit W × H numeric fields (Photoshop's "W x H x Resolution"
    // crop preset mode) — when both are filled, they override the ratio
    // preset dropdown above.
    const applyWH = (): void => {
      if (!this.tool || !wInput || !hInput) return;
      const w = parseFloat(wInput.value);
      const h = parseFloat(hInput.value);
      if (w > 0 && h > 0) this.tool.aspectRatio = w / h;
    };
    wInput?.addEventListener("change", applyWH);
    hInput?.addEventListener("change", applyWH);
    swapBtn?.addEventListener("click", () => {
      if (!wInput || !hInput) return;
      const w = wInput.value;
      const h = hInput.value;
      wInput.value = h;
      hInput.value = w;
      applyWH();
    });
    const overlayInput = this.el.querySelector<HTMLSelectElement>(".crop-overlay-input");
    overlayInput?.addEventListener("change", () => {
      if (this.tool) this.tool.overlay = overlayInput.value;
    });
    const deleteInput = this.el.querySelector<HTMLInputElement>(".crop-delete-input");
    deleteInput?.addEventListener("change", () => {
      if (this.tool) this.tool.deletePixels = deleteInput.checked;
    });
    const contentAwareInput = this.el.querySelector<HTMLInputElement>(".crop-contentaware-input");
    contentAwareInput?.addEventListener("change", () => {
      if (this.tool) this.tool.contentAware = contentAwareInput.checked;
    });
    const resolutionInput = this.el.querySelector<HTMLInputElement>(".crop-resolution-input");
    resolutionInput?.addEventListener("change", () => {
      if (this.tool) this.tool.resolution = Math.max(1, parseFloat(resolutionInput.value) || 72);
    });
    const shieldInput = this.el.querySelector<HTMLInputElement>(".crop-shield-input");
    const shieldOpacityInput = this.el.querySelector<HTMLInputElement>(".crop-shieldopacity-input");
    shieldInput?.addEventListener("change", () => {
      if (this.tool) this.tool.shieldOutside = shieldInput.checked;
      if (shieldOpacityInput) shieldOpacityInput.disabled = !shieldInput.checked;
    });
    const shieldOpacityLabel = this.el.querySelector<HTMLSpanElement>(".crop-shieldopacity-label");
    shieldOpacityInput?.addEventListener("input", () => {
      if (this.tool) this.tool.shieldOpacity = parseInt(shieldOpacityInput.value) / 100;
      if (shieldOpacityLabel) shieldOpacityLabel.textContent = `${shieldOpacityInput.value}%`;
    });
    const resetBtn = this.el.querySelector<HTMLButtonElement>(".crop-reset-btn");
    resetBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.reset === "function") {
        const doc = this.state?.doc ?? null;
        this.tool.reset(doc);
      }
    });
    const straightenBtn = this.el.querySelector<HTMLButtonElement>(".crop-straighten-btn");
    straightenBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.startStraighten === "function") {
        if (this.tool.isStraightenActive) {
          this.tool.cancelStraighten?.();
          straightenBtn.textContent = "Straighten";
        } else {
          this.tool.startStraighten();
          straightenBtn.textContent = "Cancel";
        }
      }
    });
    const commitBtn = this.el.querySelector<HTMLButtonElement>(".crop-commit-btn");
    commitBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.commit === "function") {
        this.tool.commit(this.state);
      }
    });
    const cancelBtn = this.el.querySelector<HTMLButtonElement>(".crop-cancel-btn");
    cancelBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.cancel === "function") {
        this.tool.cancel(this.state);
      }
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Free Transform Tool Options ─────────────────────────────

export class FreeTransformToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private state: any;
  /** Change callback registered on the tool — unregistered on destroy(). */
  private changeHandler: (() => void) | null = null;

  constructor(tool: any, state?: any) {
    this.tool = tool;
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const current = tool?.refPoint ?? "center";
    const aspectLock = tool?.aspectLockByDefault !== false;
    const points: Array<{ value: string; label: string }> = [
      { value: "top-left", label: "↖" },
      { value: "top", label: "↑" },
      { value: "top-right", label: "↗" },
      { value: "left", label: "←" },
      { value: "center", label: "●" },
      { value: "right", label: "→" },
      { value: "bottom-left", label: "↙" },
      { value: "bottom", label: "↓" },
      { value: "bottom-right", label: "↘" },
    ];
    this.el.innerHTML = `
      <label class="opt">Ref:
        <select class="ft-refpoint-input" style="width:36px">
          ${points.map(p => `<option value="${p.value}" ${p.value === current ? "selected" : ""}>${p.label}</option>`).join("")}
        </select>
      </label>
      <label class="opt" style="margin-left:6px">X:<input type="number" class="ft-x-input" step="1" style="width:52px" title="X position"></label>
      <label class="opt" style="margin-left:4px">Y:<input type="number" class="ft-y-input" step="1" style="width:52px" title="Y position"></label>
      <label class="opt" style="margin-left:6px">W%:<input type="number" class="ft-w-input" step="0.1" min="0.01" style="width:56px" title="Horizontal scale %"></label>
      <button class="ft-aspect-btn" type="button" title="Toggle aspect-ratio lock (post-CC2019 default: locked)" style="margin:0 2px;padding:2px 6px;cursor:pointer">${aspectLock ? "🔗" : "⛓️"}</button>
      <label class="opt">H%:<input type="number" class="ft-h-input" step="0.1" min="0.01" style="width:56px" title="Vertical scale %"></label>
      <label class="opt" style="margin-left:6px">&#8735;:<input type="number" class="ft-rot-input" step="0.1" style="width:56px" title="Rotation (degrees)"></label>
      <label class="opt" style="margin-left:4px">H:<input type="number" class="ft-skewh-input" step="0.1" style="width:52px" title="Horizontal skew (degrees)"></label>
      <label class="opt" style="margin-left:2px">V:<input type="number" class="ft-skewv-input" step="0.1" style="width:52px" title="Vertical skew (degrees)"></label>
    `;

    // Helper to get an input by selector.
    const qi = (sel: string) => this.el.querySelector<HTMLInputElement>(sel);

    const xInput    = qi(".ft-x-input");
    const yInput    = qi(".ft-y-input");
    const wInput    = qi(".ft-w-input");
    const hInput    = qi(".ft-h-input");
    const rotInput  = qi(".ft-rot-input");
    const skewhInput = qi(".ft-skewh-input");
    const skewvInput = qi(".ft-skewv-input");
    const refInput  = this.el.querySelector<HTMLSelectElement>(".ft-refpoint-input");
    const aspectBtn = this.el.querySelector<HTMLButtonElement>(".ft-aspect-btn");

    /** Re-read tool state and push values into inputs (when tool drives changes). */
    const syncFromTool = () => {
      if (!this.tool) return;
      if (xInput)     xInput.value     = String(Math.round(this.tool.posX ?? 0));
      if (yInput)     yInput.value     = String(Math.round(this.tool.posY ?? 0));
      if (wInput)     wInput.value     = String(+(this.tool.scaleXPct ?? 100).toFixed(2));
      if (hInput)     hInput.value     = String(+(this.tool.scaleYPct ?? 100).toFixed(2));
      if (rotInput)   rotInput.value   = String(+(this.tool.rotationDeg ?? 0).toFixed(2));
      if (skewhInput) skewhInput.value = String(+(this.tool.skewHDeg ?? 0).toFixed(2));
      if (skewvInput) skewvInput.value = String(+(this.tool.skewVDeg ?? 0).toFixed(2));
    };

    // Seed initial values.
    syncFromTool();

    // Register live update hook on the tool.
    this.changeHandler = syncFromTool;
    if (typeof this.tool?.onTransformChange === "function") {
      this.tool.onTransformChange(this.changeHandler);
    }

    // i107: every field-driven mutation below must re-emit a transient
    // preview command through the same ctx.emitTransient() pipeline
    // pointer-drag already uses (FreeTransformTool.onPointerMove), otherwise
    // the canvas doesn't repaint until the transform is committed/canceled.
    // FreeTransformTool.nudge(ctx, 0, 0) is the tool's existing public API
    // that rebuilds+emits the transient command from current state without
    // moving anything (dx=dy=0) — reused here instead of poking the tool's
    // private buildCommand()/emitTransient() directly.
    const emitPreview = (): void => {
      if (typeof this.tool?.nudge !== "function") return;
      const ctx = buildTransientCtxFromState(this.state);
      if (ctx) this.tool.nudge(ctx, 0, 0);
    };

    // Input → tool bindings (typing applies immediately).
    refInput?.addEventListener("change", () => {
      if (this.tool && refInput.value) this.tool.refPoint = refInput.value as any;
      emitPreview();
    });
    xInput?.addEventListener("change", () => {
      const v = parseFloat(xInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.setPosX(v); emitPreview(); }
    });
    yInput?.addEventListener("change", () => {
      const v = parseFloat(yInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.setPosY(v); emitPreview(); }
    });
    wInput?.addEventListener("change", () => {
      const v = parseFloat(wInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.scaleXPct = v; emitPreview(); }
    });
    hInput?.addEventListener("change", () => {
      const v = parseFloat(hInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.scaleYPct = v; emitPreview(); }
    });
    rotInput?.addEventListener("change", () => {
      const v = parseFloat(rotInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.rotationDeg = v; emitPreview(); }
    });
    skewhInput?.addEventListener("change", () => {
      const v = parseFloat(skewhInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.skewHDeg = v; emitPreview(); }
    });
    skewvInput?.addEventListener("change", () => {
      const v = parseFloat(skewvInput.value);
      if (!isNaN(v) && this.tool?.isActive?.()) { this.tool.skewVDeg = v; emitPreview(); }
    });

    // Aspect-lock toggle button.
    aspectBtn?.addEventListener("click", () => {
      if (!this.tool) return;
      this.tool.aspectLockByDefault = !this.tool.aspectLockByDefault;
      aspectBtn.textContent = this.tool.aspectLockByDefault ? "🔗" : "⛓️";
    });
  }

  render(): HTMLElement { return this.el; }

  destroy(): void {
    if (this.changeHandler && typeof this.tool?.offTransformChange === "function") {
      this.tool.offTransformChange(this.changeHandler);
    }
    this.changeHandler = null;
  }
}

// ── Move Tool Options ───────────────────────────────────────

// The 6 Photoshop Move-tool options-bar align buttons, in PS's own
// left-to-right order (audit i2). Each maps 1:1 to an AlignOp.
const MOVE_ALIGN_BUTTONS: ReadonlyArray<{ cls: string; op: AlignOp; title: string; label: string }> = [
  { cls: "move-align-left-btn", op: "left", title: "Align Left Edges", label: "⊢" },
  { cls: "move-align-hcenter-btn", op: "h-center", title: "Align Horizontal Centers", label: "⟺" },
  { cls: "move-align-right-btn", op: "right", title: "Align Right Edges", label: "⊣" },
  { cls: "move-align-top-btn", op: "top", title: "Align Top Edges", label: "⊤" },
  { cls: "move-align-vcenter-btn", op: "v-center", title: "Align Vertical Centers", label: "⟷" },
  { cls: "move-align-bottom-btn", op: "bottom", title: "Align Bottom Edges", label: "⊥" },
];

export class MoveToolOptions implements ToolOptions {
  private el: HTMLElement;
  private state: any;

  constructor(state?: any) {
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const autoSelect = state?.autoSelect ?? false;
    const showTransform = state?.showTransformControls ?? false;
    // i55: "Layer"/"Group" auto-select mode. Persisted as a dynamic field on
    // EditorState (state.autoSelectMode) — mirrors the "dynamic UI-only
    // field, not yet consumed" convention used throughout this file (see
    // PenToolOptions.autoAddDelete etc.). MoveTool.onPointerDown needs to
    // read this to walk up to the outermost group when mode === "group"
    // (tracked as a follow-up — MoveTool.ts is out of scope here).
    const autoSelectMode: string = state?.autoSelectMode ?? "layer";
    const alignButtonsHtml = MOVE_ALIGN_BUTTONS.map(
      (b) => `<button type="button" class="${b.cls}" title="${b.title}" style="padding:2px 8px;cursor:pointer;font-weight:bold;">${b.label}</button>`,
    ).join("");
    this.el.innerHTML = `
      <label class="opt"><input type="checkbox" class="move-autoselect-input" ${autoSelect ? "checked" : ""}> Auto-Select
        <select class="move-autoselect-mode" ${autoSelect ? "" : "disabled"}>
          <option value="layer" ${autoSelectMode === "layer" ? "selected" : ""}>Layer</option>
          <option value="group" ${autoSelectMode === "group" ? "selected" : ""}>Group</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" class="move-transform-input" ${showTransform ? "checked" : ""}> Show Transform Controls</label>
      <span class="optionsbar__align-group" style="margin-left:10px;display:inline-flex;gap:2px;">${alignButtonsHtml}</span>
    `;
    const autoSelectInput = this.el.querySelector<HTMLInputElement>(".move-autoselect-input");
    const autoSelectModeInput = this.el.querySelector<HTMLSelectElement>(".move-autoselect-mode");
    autoSelectInput?.addEventListener("change", () => {
      if (state) state.autoSelect = autoSelectInput.checked;
      if (autoSelectModeInput) autoSelectModeInput.disabled = !autoSelectInput.checked;
    });
    autoSelectModeInput?.addEventListener("change", () => {
      if (state) state.autoSelectMode = autoSelectModeInput.value;
    });
    const transformInput = this.el.querySelector<HTMLInputElement>(".move-transform-input");
    transformInput?.addEventListener("change", () => {
      if (state) state.showTransformControls = transformInput.checked;
    });

    // Align buttons dispatch the same AlignLayersCommand the Layer > Align
    // menu items use, against the current active/selected layers (i2).
    for (const b of MOVE_ALIGN_BUTTONS) {
      this.el.querySelector<HTMLButtonElement>(`.${b.cls}`)?.addEventListener("click", () => {
        const st = this.state;
        const doc = st?.doc;
        if (!doc) return;
        const ids = collectLayerTargetIds(doc.activeLayerId, st.selectedLayerIds ?? new Set());
        const cmd = planAlignLayers(doc, ids, b.op);
        if (cmd) st.execute(cmd);
      });
    }
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Pen Tool Options ────────────────────────────────────────
//
// The "Mode" dropdown here controls the drawing sub-mode (PenMode), which
// determines how anchors and handles are placed. Valid values are exactly the
// members of the PenMode union: "pen" | "freeform" | "curvature" |
// "add-anchor" | "delete-anchor" | "convert-point".
//
// NOTE: PenTool does not yet have `autoAddDelete` or `rubberBand` fields.
// Those checkboxes write to `tool.autoAddDelete` and `tool.rubberBand`
// respectively via dynamic assignment; the render consumer is still pending
// (tracked in issue #208).

export class PenToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    // Reflect the current PenMode on regen — default to "pen" if unset or invalid.
    const PEN_MODES = ["pen", "freeform", "curvature", "add-anchor", "delete-anchor", "convert-point"] as const;
    const rawMode: string = tool?.mode ?? "pen";
    const modeValue = (PEN_MODES as readonly string[]).includes(rawMode) ? rawMode : "pen";
    const autoAdd = tool?.autoAddDelete ?? false;
    const rubberBand = tool?.rubberBand ?? false;
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="pen-mode-input">
          <option value="pen" ${modeValue === "pen" ? "selected" : ""}>Pen</option>
          <option value="freeform" ${modeValue === "freeform" ? "selected" : ""}>Freeform</option>
          <option value="curvature" ${modeValue === "curvature" ? "selected" : ""}>Curvature</option>
          <option value="add-anchor" ${modeValue === "add-anchor" ? "selected" : ""}>Add Anchor</option>
          <option value="delete-anchor" ${modeValue === "delete-anchor" ? "selected" : ""}>Delete Anchor</option>
          <option value="convert-point" ${modeValue === "convert-point" ? "selected" : ""}>Convert Point</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" class="pen-autoadd-input" ${autoAdd ? "checked" : ""}> Auto Add/Delete</label>
      <label class="opt"><input type="checkbox" class="pen-rubberband-input" ${rubberBand ? "checked" : ""}> Rubber Band</label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".pen-mode-input");
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value;
    });
    const autoAddInput = this.el.querySelector<HTMLInputElement>(".pen-autoadd-input");
    autoAddInput?.addEventListener("change", () => {
      if (this.tool) this.tool.autoAddDelete = autoAddInput.checked;
    });
    const rubberBandInput = this.el.querySelector<HTMLInputElement>(".pen-rubberband-input");
    rubberBandInput?.addEventListener("change", () => {
      if (this.tool) this.tool.rubberBand = rubberBandInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Shape Tool Options ──────────────────────────────────────
//
// Also handles the shape flyout sub-tools (#86): shape-rounded-rect,
// shape-polygon, shape-triangle and shape-line are all backed by ShapeTool
// instances (see main.ts) that differ only in `mode`/preset fields, and
// shape-custom is backed by CustomShapeTool (fillColor/strokeColor/
// strokeWidth compatible, but no `mode` — it exposes `preset` instead).
// Below the common Tool Mode/Fill/Stroke/Stroke Width row, this class adds
// mode-specific geometry controls (#92) wired to ShapeTool's real fields:
// cornerRadius (rect/rounded-rect), polygonSides/polygonStar/
// polygonStarInnerRatio (polygon/triangle), lineArrowhead/lineArrowSize
// (line), and a preset picker for CustomShapeTool.

const CUSTOM_SHAPE_PRESETS = [
  "heart", "star-5", "star-6", "star-8", "flower", "arrow-right", "arrow-left",
  "arrow-up", "arrow-down", "speech-bubble", "cloud", "lightning", "checkmark",
  "cross", "circle-badge", "shield", "gear", "moon", "sun", "bookmark",
] as const;

function customShapePresetLabel(preset: string): string {
  return preset.split("-").map((w) => (w[0] ?? "").toUpperCase() + w.slice(1)).join(" ");
}

export class ShapeToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    // i85: `tool.shapeToolMode` is a new 3-way dynamic field ("shape"/
    // "path"/"pixels") that remembers Path vs Pixels distinctly — before
    // this, both collapsed onto `!tool.createVector` and the dropdown always
    // showed "Path" again after a rebuild even if "Pixels" had been picked.
    // `tool.createVector` is kept in sync (true only for "shape") since
    // main.ts's pointerup handler still branches on it; routing "path" into
    // an actual work-path (instead of a shape layer or rasterized pixels) is
    // a main.ts change tracked as a follow-up (out of scope here).
    const modeValue: string = typeof tool?.shapeToolMode === "string"
      ? tool.shapeToolMode
      : (tool?.createVector ? "shape" : "path");
    const fillColorHex = this.rgbToHex(tool?.fillColor) ?? "#000000";
    const strokeColorHex = this.rgbToHex(tool?.strokeColor) ?? "#000000";
    const strokeWidth = tool?.strokeWidth ?? 2;
    const shapeMode: string | undefined = tool?.mode;

    let geometryHtml = "";
    if (shapeMode === "rect") {
      const cornerRadius = tool?.cornerRadius ?? 0;
      geometryHtml = `<label class="opt">Corner Radius: <input type="number" min="0" max="2000" value="${cornerRadius}" class="shape-cornerradius-input" style="width:56px"></label>`;
    } else if (shapeMode === "polygon") {
      const sides = tool?.polygonSides ?? 5;
      const star = tool?.polygonStar === true;
      const innerRatioPct = Math.round((tool?.polygonStarInnerRatio ?? 0.5) * 100);
      geometryHtml = `
        <label class="opt">Sides: <input type="number" min="3" max="100" value="${sides}" class="shape-polysides-input" style="width:48px"></label>
        <label class="opt"><input type="checkbox" ${star ? "checked" : ""} class="shape-polystar-input"> Star</label>
        <label class="opt">Indent: <input type="number" min="1" max="99" value="${innerRatioPct}" class="shape-starratio-input" style="width:48px">%</label>
      `;
    } else if (shapeMode === "line") {
      const arrowhead = tool?.lineArrowhead === true;
      const arrowSize = tool?.lineArrowSize ?? 15;
      geometryHtml = `
        <label class="opt"><input type="checkbox" ${arrowhead ? "checked" : ""} class="shape-linearrow-input"> Arrowheads</label>
        <label class="opt">Arrow Size: <input type="number" min="1" max="1000" value="${arrowSize}" class="shape-arrowsize-input" style="width:56px"></label>
      `;
    } else if (shapeMode === undefined && typeof tool?.preset === "string") {
      geometryHtml = `
        <label class="opt">Shape:
          <select class="shape-custompreset-input">
            ${CUSTOM_SHAPE_PRESETS.map((p) => `<option value="${p}" ${tool.preset === p ? "selected" : ""}>${customShapePresetLabel(p)}</option>`).join("")}
          </select>
        </label>
      `;
    }

    this.el.innerHTML = `
      <label class="opt">Tool Mode:
        <select class="shape-mode-input">
          <option value="shape" ${modeValue === "shape" ? "selected" : ""}>Shape</option>
          <option value="path" ${modeValue === "path" ? "selected" : ""}>Path</option>
          <option value="pixels" ${modeValue === "pixels" ? "selected" : ""}>Pixels</option>
        </select>
      </label>
      <label class="opt">Fill: <input type="color" class="shape-fill-input" value="${fillColorHex}"></label>
      <label class="opt">Stroke: <input type="color" class="shape-stroke-input" value="${strokeColorHex}"></label>
      <label class="opt">Stroke Width: <input type="number" min="0" max="1000" value="${strokeWidth}" class="shape-strokewidth-input" style="width:50px"></label>
      ${geometryHtml}
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".shape-mode-input");
    modeInput?.addEventListener("change", () => {
      if (!this.tool) return;
      this.tool.shapeToolMode = modeInput.value;
      this.tool.createVector = modeInput.value === "shape";
    });
    const fillInput = this.el.querySelector<HTMLInputElement>(".shape-fill-input");
    fillInput?.addEventListener("input", () => {
      if (this.tool) this.tool.fillColor = this.hexToRgb(fillInput.value);
    });
    const strokeInput = this.el.querySelector<HTMLInputElement>(".shape-stroke-input");
    strokeInput?.addEventListener("input", () => {
      if (this.tool) this.tool.strokeColor = this.hexToRgb(strokeInput.value);
    });
    const strokeWidthInput = this.el.querySelector<HTMLInputElement>(".shape-strokewidth-input");
    strokeWidthInput?.addEventListener("change", () => {
      if (this.tool) this.tool.strokeWidth = parseInt(strokeWidthInput.value) || 0;
    });

    // Geometry controls (#86, #92).
    const cornerRadiusInput = this.el.querySelector<HTMLInputElement>(".shape-cornerradius-input");
    cornerRadiusInput?.addEventListener("change", () => {
      if (this.tool) this.tool.cornerRadius = Math.max(0, parseFloat(cornerRadiusInput.value) || 0);
    });
    const polySidesInput = this.el.querySelector<HTMLInputElement>(".shape-polysides-input");
    polySidesInput?.addEventListener("change", () => {
      if (this.tool) this.tool.polygonSides = Math.max(3, parseInt(polySidesInput.value) || 3);
    });
    const polyStarInput = this.el.querySelector<HTMLInputElement>(".shape-polystar-input");
    polyStarInput?.addEventListener("change", () => {
      if (this.tool) this.tool.polygonStar = polyStarInput.checked;
    });
    const starRatioInput = this.el.querySelector<HTMLInputElement>(".shape-starratio-input");
    starRatioInput?.addEventListener("change", () => {
      if (this.tool) this.tool.polygonStarInnerRatio = Math.max(0.01, Math.min(0.99, (parseFloat(starRatioInput.value) || 50) / 100));
    });
    const lineArrowInput = this.el.querySelector<HTMLInputElement>(".shape-linearrow-input");
    lineArrowInput?.addEventListener("change", () => {
      if (this.tool) this.tool.lineArrowhead = lineArrowInput.checked;
    });
    const arrowSizeInput = this.el.querySelector<HTMLInputElement>(".shape-arrowsize-input");
    arrowSizeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.lineArrowSize = Math.max(1, parseFloat(arrowSizeInput.value) || 15);
    });
    const customPresetInput = this.el.querySelector<HTMLSelectElement>(".shape-custompreset-input");
    customPresetInput?.addEventListener("change", () => {
      if (this.tool) this.tool.preset = customPresetInput.value;
    });
  }

  /**
   * Re-read the live tool's fill/stroke/preset into the already-rendered
   * controls without rebuilding the DOM (mirrors the ToolOptions.refresh()
   * contract used elsewhere in this file). Needed for audit i91 / #269: the
   * new Shapes panel (main.ts's openShapesPanel()) sets
   * `customShapeTool2.preset` directly when a user picks a preset there, and
   * calls `toolUx.refreshOptions()` afterwards so this options bar's
   * `.shape-custompreset-input` select reflects the change instead of
   * showing a stale preset.
   */
  refresh(): void {
    const presetInput = this.el.querySelector<HTMLSelectElement>(".shape-custompreset-input");
    if (presetInput && typeof this.tool?.preset === "string") presetInput.value = this.tool.preset;
    const fillInput = this.el.querySelector<HTMLInputElement>(".shape-fill-input");
    const fillHex = this.rgbToHex(this.tool?.fillColor);
    if (fillInput && fillHex) fillInput.value = fillHex;
    const strokeInput = this.el.querySelector<HTMLInputElement>(".shape-stroke-input");
    const strokeHex = this.rgbToHex(this.tool?.strokeColor);
    if (strokeInput && strokeHex) strokeInput.value = strokeHex;
    const strokeWidthInput = this.el.querySelector<HTMLInputElement>(".shape-strokewidth-input");
    if (strokeWidthInput && typeof this.tool?.strokeWidth === "number") strokeWidthInput.value = String(this.tool.strokeWidth);
  }

  private rgbToHex(color: { r: number; g: number; b: number; a: number } | null | undefined): string | null {
    if (!color) return null;
    const r = Math.round(color.r).toString(16).padStart(2, "0");
    const g = Math.round(color.g).toString(16).padStart(2, "0");
    const b = Math.round(color.b).toString(16).padStart(2, "0");
    return `#${r}${g}${b}`;
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number; a: number } {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b, a: 1 };
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Gradient Tool Options ───────────────────────────────────

export class GradientToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const shape = tool?.shape ?? "linear";
    const preset = tool?.preset ?? "fg-bg";
    const opacity = Math.round((tool?.opacity ?? 1) * 100);
    const reverse = tool?.reverse === true;
    const dither = tool?.dither !== false;
    // i185: both fields already exist and are consumed by GradientTool —
    // `transparency` (defaults true) and the "black-white" preset value —
    // only the options-bar controls for them were missing.
    const transparency = tool?.transparency !== false;
    this.el.innerHTML = `
      <label class="opt">Shape:
        <select class="grad-shape-input">
          <option value="linear" ${shape === "linear" ? "selected" : ""}>Linear</option>
          <option value="radial" ${shape === "radial" ? "selected" : ""}>Radial</option>
          <option value="angle" ${shape === "angle" ? "selected" : ""}>Angle</option>
          <option value="reflected" ${shape === "reflected" ? "selected" : ""}>Reflected</option>
          <option value="diamond" ${shape === "diamond" ? "selected" : ""}>Diamond</option>
        </select>
      </label>
      <label class="opt">Preset:
        <select class="grad-preset-input">
          <option value="fg-bg" ${preset === "fg-bg" ? "selected" : ""}>Foreground to Background</option>
          <option value="fg-transparent" ${preset === "fg-transparent" ? "selected" : ""}>Foreground to Transparent</option>
          <option value="bg-transparent" ${preset === "bg-transparent" ? "selected" : ""}>Background to Transparent</option>
          <option value="black-white" ${preset === "black-white" ? "selected" : ""}>Black to White</option>
        </select>
      </label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${opacity}" class="grad-opacity-input"> <span class="grad-opacity-label">${opacity}%</span></label>
      <label class="opt"><input type="checkbox" ${reverse ? "checked" : ""} class="grad-reverse-input"> Reverse</label>
      <label class="opt"><input type="checkbox" ${dither ? "checked" : ""} class="grad-dither-input"> Dither</label>
      <label class="opt"><input type="checkbox" ${transparency ? "checked" : ""} class="grad-transparency-input"> Transparency</label>
    `;
    const shapeInput = this.el.querySelector<HTMLSelectElement>(".grad-shape-input");
    const presetInput = this.el.querySelector<HTMLSelectElement>(".grad-preset-input");
    const opacityInput = this.el.querySelector<HTMLInputElement>(".grad-opacity-input");
    const opacityLabel = this.el.querySelector<HTMLSpanElement>(".grad-opacity-label");
    const reverseInput = this.el.querySelector<HTMLInputElement>(".grad-reverse-input");
    const ditherInput = this.el.querySelector<HTMLInputElement>(".grad-dither-input");
    const transparencyInput = this.el.querySelector<HTMLInputElement>(".grad-transparency-input");

    shapeInput?.addEventListener("change", () => { if (this.tool) this.tool.shape = shapeInput.value; });
    presetInput?.addEventListener("change", () => { if (this.tool) this.tool.preset = presetInput.value; });
    opacityInput?.addEventListener("input", () => {
      if (this.tool) this.tool.opacity = parseInt(opacityInput.value) / 100;
      if (opacityLabel) opacityLabel.textContent = `${opacityInput.value}%`;
    });
    reverseInput?.addEventListener("change", () => { if (this.tool) this.tool.reverse = reverseInput.checked; });
    ditherInput?.addEventListener("change", () => { if (this.tool) this.tool.dither = ditherInput.checked; });
    transparencyInput?.addEventListener("change", () => { if (this.tool) this.tool.transparency = transparencyInput.checked; });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Paint Bucket Tool Options ───────────────────────────────

export class PaintBucketToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const tol = tool?.tolerance ?? 32;
    const contiguous = tool?.contiguous !== false;
    const aa = tool?.antiAlias !== false;
    const opacity = Math.round((tool?.opacity ?? 1) * 100);
    // i190: `blendMode`/`sampleAllLayers`/`fillSource` already exist on
    // PaintBucketTool and are consumed by fill()/sourceColorAt() — only the
    // options-bar controls for them were missing. Paint Bucket is one of the
    // named consumers of PAINTING_BLEND_MODES (see doc comment above).
    const blendMode = tool?.blendMode ?? "normal";
    const sampleAllLayers = tool?.sampleAllLayers === true;
    const fillSource = tool?.fillSource ?? "foreground";
    this.el.innerHTML = `
      <label class="opt">Fill:
        <select class="bucket-fillsource-input">
          <option value="foreground" ${fillSource === "foreground" ? "selected" : ""}>Foreground</option>
          <option value="pattern" ${fillSource === "pattern" ? "selected" : ""}>Pattern</option>
        </select>
      </label>
      <label class="opt">Mode:
        <select class="bucket-mode-input">
          ${buildPaintingBlendModeOptions(blendMode)}
        </select>
      </label>
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${tol}" class="bucket-tolerance-input" style="width:50px"></label>
      <label class="opt"><input type="checkbox" ${aa ? "checked" : ""} class="bucket-antialias-input"> Anti-alias</label>
      <label class="opt"><input type="checkbox" ${contiguous ? "checked" : ""} class="bucket-contiguous-input"> Contiguous</label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="bucket-sampleall-input"> Sample All Layers</label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${opacity}" class="bucket-opacity-input"> <span class="bucket-opacity-label">${opacity}%</span></label>
    `;
    const fillSourceInput = this.el.querySelector<HTMLSelectElement>(".bucket-fillsource-input");
    const modeInput = this.el.querySelector<HTMLSelectElement>(".bucket-mode-input");
    const tolInput = this.el.querySelector<HTMLInputElement>(".bucket-tolerance-input");
    const aaInput = this.el.querySelector<HTMLInputElement>(".bucket-antialias-input");
    const contInput = this.el.querySelector<HTMLInputElement>(".bucket-contiguous-input");
    const sampleAllInput = this.el.querySelector<HTMLInputElement>(".bucket-sampleall-input");
    const opacityInput = this.el.querySelector<HTMLInputElement>(".bucket-opacity-input");
    const opacityLabel = this.el.querySelector<HTMLSpanElement>(".bucket-opacity-label");

    fillSourceInput?.addEventListener("change", () => {
      if (this.tool) this.tool.fillSource = fillSourceInput.value as "foreground" | "pattern";
    });
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.blendMode = modeInput.value;
    });
    tolInput?.addEventListener("change", () => {
      if (this.tool) this.tool.tolerance = Math.max(0, Math.min(255, parseInt(tolInput.value) || 0));
    });
    aaInput?.addEventListener("change", () => { if (this.tool) this.tool.antiAlias = aaInput.checked; });
    contInput?.addEventListener("change", () => { if (this.tool) this.tool.contiguous = contInput.checked; });
    sampleAllInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllInput.checked;
    });
    opacityInput?.addEventListener("input", () => {
      if (this.tool) this.tool.opacity = parseInt(opacityInput.value) / 100;
      if (opacityLabel) opacityLabel.textContent = `${opacityInput.value}%`;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Retouch Tool Options (Blur / Sharpen / Smudge) ──────────

export class RetouchToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const mode = tool?.mode ?? "blur";
    const strength = tool?.strength ?? 50;
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 1 };
    // i175: `sampleAllLayers`/`fingerPainting` already exist on RetouchTool
    // and are consumed by the Smudge code path — only the options-bar
    // checkboxes were missing. Both are smudge-only in Photoshop (grayed out
    // for Blur/Sharpen), so start disabled unless the current mode is smudge.
    const sampleAllLayers = tool?.sampleAllLayers === true;
    const fingerPainting = tool?.fingerPainting === true;
    const smudgeOnly = mode === "smudge";
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="retouch-mode-input">
          <option value="blur" ${mode === "blur" ? "selected" : ""}>Blur</option>
          <option value="sharpen" ${mode === "sharpen" ? "selected" : ""}>Sharpen</option>
          <option value="smudge" ${mode === "smudge" ? "selected" : ""}>Smudge</option>
        </select>
      </label>
      <label class="opt">Strength: <input type="range" min="0" max="100" value="${strength}" class="retouch-strength-input"> <span class="retouch-strength-label">${strength}%</span></label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="retouch-size-input"> <span class="retouch-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="retouch-hardness-input"> <span class="retouch-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} ${smudgeOnly ? "" : "disabled"} class="retouch-sampleall-input"> Sample All Layers</label>
      <label class="opt"><input type="checkbox" ${fingerPainting ? "checked" : ""} ${smudgeOnly ? "" : "disabled"} class="retouch-fingerpaint-input"> Finger Painting</label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".retouch-mode-input");
    const strengthInput = this.el.querySelector<HTMLInputElement>(".retouch-strength-input");
    const strengthLabel = this.el.querySelector<HTMLSpanElement>(".retouch-strength-label");
    const sizeInput = this.el.querySelector<HTMLInputElement>(".retouch-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".retouch-size-label");
    const hardnessInput = this.el.querySelector<HTMLInputElement>(".retouch-hardness-input");
    const hardnessLabel = this.el.querySelector<HTMLSpanElement>(".retouch-hardness-label");
    const sampleAllInput = this.el.querySelector<HTMLInputElement>(".retouch-sampleall-input");
    const fingerPaintInput = this.el.querySelector<HTMLInputElement>(".retouch-fingerpaint-input");

    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value;
      const isSmudge = modeInput.value === "smudge";
      if (sampleAllInput) sampleAllInput.disabled = !isSmudge;
      if (fingerPaintInput) fingerPaintInput.disabled = !isSmudge;
    });
    sampleAllInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllInput.checked;
    });
    fingerPaintInput?.addEventListener("change", () => {
      if (this.tool) this.tool.fingerPainting = fingerPaintInput.checked;
    });
    strengthInput?.addEventListener("input", () => {
      if (this.tool) this.tool.strength = parseInt(strengthInput.value) || 0;
      if (strengthLabel) strengthLabel.textContent = `${strengthInput.value}%`;
    });
    sizeInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
      this.onChange();
    });
    hardnessInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.hardness = (parseInt(hardnessInput.value) || 0) / 100;
      if (hardnessLabel) hardnessLabel.textContent = `${hardnessInput.value}%`;
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── History Brush Tool Options ──────────────────────────────

export class HistoryBrushToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 1 };
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="hbrush-size-input"> <span class="hbrush-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="hbrush-hardness-input"> <span class="hbrush-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 1) * 100)}" class="hbrush-opacity-input"> <span class="hbrush-opacity-label">${Math.round((s.opacity ?? 1) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 1) * 100)}" class="hbrush-flow-input"> <span class="hbrush-flow-label">${Math.round((s.flow ?? 1) * 100)}%</span></label>
    `;
    this.bindSlider(".hbrush-size-input", ".hbrush-size-label", "size", 1, false);
    this.bindSlider(".hbrush-hardness-input", ".hbrush-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".hbrush-opacity-input", ".hbrush-opacity-label", "opacity", 0.01, true);
    this.bindSlider(".hbrush-flow-input", ".hbrush-flow-label", "flow", 0.01, true);
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Type Tool Options (#188) ────────────────────────────────
//
// Photoshop-matching options bar for all Type tool variants.
// Controls wire to settable fields on the tool instance (dynamic assignment):
//   tool.fontFamily, tool.fontSize, tool.antiAlias, tool.textAlign,
//   tool.fauxBold, tool.fauxItalic
// These fields are not yet declared on TypeTool — they are applied via dynamic
// assignment and consumed when the host creates a new TypeLayer. If TypeTool
// gains explicit typed fields in the future, no ToolUX changes are required.

const WEB_SAFE_FONTS = [
  "Arial", "Arial Black", "Comic Sans MS", "Courier New", "Georgia",
  "Impact", "Lucida Console", "Lucida Sans Unicode", "Palatino Linotype",
  "Tahoma", "Times New Roman", "Trebuchet MS", "Verdana",
];

const STANDARD_FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 21, 24, 28, 32, 36, 42, 48, 56, 64, 72, 96, 120];

function buildFontFamilyOptions(selected: string): string {
  const families: string[] = [...WEB_SAFE_FONTS];
  // Append any loaded fonts from document.fonts that are not already listed.
  if (typeof document !== "undefined" && document.fonts) {
    try {
      document.fonts.forEach((face) => {
        const fam = face.family.replace(/^["']|["']$/g, "");
        if (!families.includes(fam)) families.push(fam);
      });
    } catch {
      // document.fonts.forEach is not available in jsdom — ignore.
    }
  }
  return families.map((f) => {
    const sel = f === selected ? " selected" : "";
    return `<option value="${f}"${sel}>${f}</option>`;
  }).join("");
}

export class TypeToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private state: any;

  constructor(tool: any, state?: any) {
    this.tool = tool;
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const fontFamily: string = tool?.fontFamily ?? "Arial";
    const fontSize: number = tool?.fontSize ?? 36;
    const antiAlias: string = tool?.antiAlias ?? "smooth";
    const textAlign: string = tool?.textAlign ?? "left";
    const fauxBold: boolean = tool?.fauxBold === true;
    const fauxItalic: boolean = tool?.fauxItalic === true;
    // i14: Warp button only makes sense against an active type layer — grey
    // it out otherwise (openWarpTextDialogForActiveLayer no-ops either way).
    const hasActiveTypeLayer = !!getActiveTypeLayerWarp(state);
    const fontSizeOptions = STANDARD_FONT_SIZES.map((s) =>
      `<option value="${s}" ${s === fontSize ? "selected" : ""}>${s}</option>`,
    ).join("");
    this.el.innerHTML = `
      <label class="opt">Font:
        <select class="type-fontfamily-input" style="max-width:160px">
          ${buildFontFamilyOptions(fontFamily)}
        </select>
      </label>
      <label class="opt">Size:
        <input type="number" min="1" max="1296" value="${fontSize}" class="type-fontsize-input" style="width:52px">
        <select class="type-fontsize-preset-input" style="width:60px" aria-label="Size preset">
          ${fontSizeOptions}
        </select>
      </label>
      <label class="opt">Anti-alias:
        <select class="type-antialias-input">
          <option value="none" ${antiAlias === "none" ? "selected" : ""}>None</option>
          <option value="sharp" ${antiAlias === "sharp" ? "selected" : ""}>Sharp</option>
          <option value="crisp" ${antiAlias === "crisp" ? "selected" : ""}>Crisp</option>
          <option value="strong" ${antiAlias === "strong" ? "selected" : ""}>Strong</option>
          <option value="smooth" ${antiAlias === "smooth" ? "selected" : ""}>Smooth</option>
        </select>
      </label>
      <span class="type-align-group">
        <button type="button" class="type-align-btn opt-btn ${textAlign === "left" ? "is-active" : ""}" data-align="left" title="Align Left" aria-pressed="${textAlign === "left"}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><line x1="2" y1="3" x2="12" y2="3"/><line x1="2" y1="6" x2="9" y2="6"/><line x1="2" y1="9" x2="12" y2="9"/><line x1="2" y1="12" x2="8" y2="12"/></svg></button>
        <button type="button" class="type-align-btn opt-btn ${textAlign === "center" ? "is-active" : ""}" data-align="center" title="Align Center" aria-pressed="${textAlign === "center"}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><line x1="2" y1="3" x2="12" y2="3"/><line x1="3.5" y1="6" x2="10.5" y2="6"/><line x1="2" y1="9" x2="12" y2="9"/><line x1="4" y1="12" x2="10" y2="12"/></svg></button>
        <button type="button" class="type-align-btn opt-btn ${textAlign === "right" ? "is-active" : ""}" data-align="right" title="Align Right" aria-pressed="${textAlign === "right"}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><line x1="2" y1="3" x2="12" y2="3"/><line x1="5" y1="6" x2="12" y2="6"/><line x1="2" y1="9" x2="12" y2="9"/><line x1="6" y1="12" x2="12" y2="12"/></svg></button>
      </span>
      <button type="button" class="type-fauxbold-btn opt-btn ${fauxBold ? "is-active" : ""}" title="Faux Bold" aria-pressed="${fauxBold}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><path d="M3 2h5.5a3 3 0 0 1 0 6H3zm0 6h6a3 3 0 0 1 0 6H3z"/></svg></button>
      <button type="button" class="type-fauxitalic-btn opt-btn ${fauxItalic ? "is-active" : ""}" title="Faux Italic" aria-pressed="${fauxItalic}"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><line x1="8" y1="2" x2="6" y2="12"/><line x1="5" y1="2" x2="10" y2="2"/><line x1="4" y1="12" x2="9" y2="12"/></svg></button>
      <button type="button" class="type-warp-btn opt-btn" title="Create warped text" aria-label="Warp Text" ${hasActiveTypeLayer ? "" : "disabled"}><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M2 10 Q7 2 12 10"/></svg></button>
      <button type="button" class="type-commit-btn opt-btn" title="Commit text edit (Enter)" style="margin-left:12px;font-weight:bold;">&#10003;</button>
      <button type="button" class="type-cancel-btn opt-btn" title="Cancel text edit (Esc)" style="margin-left:2px;">&#10005;</button>
    `;

    const familyInput = this.el.querySelector<HTMLSelectElement>(".type-fontfamily-input");
    familyInput?.addEventListener("change", () => {
      if (this.tool) this.tool.fontFamily = familyInput.value;
    });

    const sizeInput = this.el.querySelector<HTMLInputElement>(".type-fontsize-input");
    const sizePreset = this.el.querySelector<HTMLSelectElement>(".type-fontsize-preset-input");
    sizeInput?.addEventListener("change", () => {
      const v = Math.max(1, Math.min(1296, parseInt(sizeInput.value) || 36));
      sizeInput.value = String(v);
      if (this.tool) this.tool.fontSize = v;
    });
    sizePreset?.addEventListener("change", () => {
      const v = parseInt(sizePreset.value) || 36;
      if (sizeInput) sizeInput.value = String(v);
      if (this.tool) this.tool.fontSize = v;
    });

    const aaInput = this.el.querySelector<HTMLSelectElement>(".type-antialias-input");
    aaInput?.addEventListener("change", () => {
      if (this.tool) this.tool.antiAlias = aaInput.value;
    });

    const alignBtns = this.el.querySelectorAll<HTMLButtonElement>(".type-align-btn");
    alignBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const align = btn.dataset["align"] ?? "left";
        if (this.tool) this.tool.textAlign = align;
        alignBtns.forEach((b) => {
          const isActive = b.dataset["align"] === align;
          b.classList.toggle("is-active", isActive);
          b.setAttribute("aria-pressed", isActive ? "true" : "false");
        });
      });
    });

    const boldBtn = this.el.querySelector<HTMLButtonElement>(".type-fauxbold-btn");
    boldBtn?.addEventListener("click", () => {
      const next = !(this.tool?.fauxBold === true);
      if (this.tool) this.tool.fauxBold = next;
      boldBtn.classList.toggle("is-active", next);
      boldBtn.setAttribute("aria-pressed", next ? "true" : "false");
    });

    const italicBtn = this.el.querySelector<HTMLButtonElement>(".type-fauxitalic-btn");
    italicBtn?.addEventListener("click", () => {
      const next = !(this.tool?.fauxItalic === true);
      if (this.tool) this.tool.fauxItalic = next;
      italicBtn.classList.toggle("is-active", next);
      italicBtn.setAttribute("aria-pressed", next ? "true" : "false");
    });

    // i14: Warp Text — second entry point alongside Layer > Type > Warp
    // Text… (menus.ts / main.ts's showWarpTextDialog). Both funnel through
    // openWarpTextDialogForActiveLayer so behavior stays identical; this one
    // reaches app state directly (the same convention MoveToolOptions /
    // CropToolOptions already use), rather than a bespoke callback channel.
    const warpBtn = this.el.querySelector<HTMLButtonElement>(".type-warp-btn");
    warpBtn?.addEventListener("click", () => {
      openWarpTextDialogForActiveLayer({
        getActiveTypeLayer: () => getActiveTypeLayerWarp(this.state),
        onApply: (layerId, warp) => {
          this.state?.execute(new SetLayerPropertyCommand(layerId, { warp }));
          this.state?.requestRender?.();
        },
      });
    });

    // i96: commit (✓) / cancel (✕) buttons for the active text-edit session.
    // ToolUX has no reference to main.ts's TextEditorOverlay instance (it's a
    // local const in main.ts, never attached to EditorState), so this
    // dispatches bubbling custom events rather than calling
    // textEditor.commitNow()/cancel() directly — same placeholder-event
    // convention as BrushToolOptions's `.brush-preset-btn`
    // (pixelpa:open-brush-presets). main.ts needs to add a listener (or
    // expose the textEditor reference some other way) to wire these up, and
    // ideally hide/show the buttons based on `textEditor.isActive()`
    // (tracked as a follow-up — TextEditorOverlay.ts/main.ts are out of
    // scope here).
    const commitBtn = this.el.querySelector<HTMLButtonElement>(".type-commit-btn");
    commitBtn?.addEventListener("click", () => {
      const evt = new CustomEvent("pixelpa:type-commit", { bubbles: true });
      this.el.dispatchEvent(evt);
    });
    const cancelBtn = this.el.querySelector<HTMLButtonElement>(".type-cancel-btn");
    cancelBtn?.addEventListener("click", () => {
      const evt = new CustomEvent("pixelpa:type-cancel", { bubbles: true });
      this.el.dispatchEvent(evt);
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Lasso / Polygonal Lasso Options (#189) ──────────────────
//
// Feather and Anti-alias wired to tool.feather and tool.antiAlias.
// Neither field is declared on LassoTool/PolygonalLassoTool — they are
// applied via dynamic assignment and consumed by selection commit code.

export class LassoToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const feather: number = tool?.feather ?? 0;
    const antiAlias: boolean = tool?.antiAlias !== false;
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Feather: <input type="number" min="0" max="1000" value="${feather}" class="lasso-feather-input" style="width:50px"> px</label>
      <label class="opt"><input type="checkbox" class="lasso-antialias-input" ${antiAlias ? "checked" : ""}> Anti-alias</label>
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    const featherInput = this.el.querySelector<HTMLInputElement>(".lasso-feather-input");
    featherInput?.addEventListener("change", () => {
      if (this.tool) this.tool.feather = Math.max(0, parseFloat(featherInput.value) || 0);
    });
    const aaInput = this.el.querySelector<HTMLInputElement>(".lasso-antialias-input");
    aaInput?.addEventListener("change", () => {
      if (this.tool) this.tool.antiAlias = aaInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Magnetic Lasso Options (#189) ───────────────────────────
//
// Feather, Anti-alias (same as Lasso) plus Width, Contrast, Frequency
// wired to MagneticLassoTool.width / .contrast / .frequency.

export class MagneticLassoToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const feather: number = tool?.feather ?? 0;
    const antiAlias: boolean = tool?.antiAlias !== false;
    const width: number = tool?.width ?? 10;
    const contrast: number = tool?.contrast ?? 50;
    const frequency: number = tool?.frequency ?? 50;
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Feather: <input type="number" min="0" max="1000" value="${feather}" class="maglasso-feather-input" style="width:50px"> px</label>
      <label class="opt"><input type="checkbox" class="maglasso-antialias-input" ${antiAlias ? "checked" : ""}> Anti-alias</label>
      <label class="opt">Width: <input type="number" min="1" max="256" value="${width}" class="maglasso-width-input" style="width:44px"> px</label>
      <label class="opt">Contrast: <input type="number" min="1" max="100" value="${contrast}" class="maglasso-contrast-input" style="width:44px"> %</label>
      <label class="opt">Frequency: <input type="number" min="0" max="100" value="${frequency}" class="maglasso-frequency-input" style="width:44px"></label>
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    const featherInput = this.el.querySelector<HTMLInputElement>(".maglasso-feather-input");
    featherInput?.addEventListener("change", () => {
      if (this.tool) this.tool.feather = Math.max(0, parseFloat(featherInput.value) || 0);
    });
    const aaInput = this.el.querySelector<HTMLInputElement>(".maglasso-antialias-input");
    aaInput?.addEventListener("change", () => {
      if (this.tool) this.tool.antiAlias = aaInput.checked;
    });
    const widthInput = this.el.querySelector<HTMLInputElement>(".maglasso-width-input");
    widthInput?.addEventListener("change", () => {
      if (this.tool) this.tool.width = Math.max(1, Math.min(256, parseInt(widthInput.value) || 10));
    });
    const contrastInput = this.el.querySelector<HTMLInputElement>(".maglasso-contrast-input");
    contrastInput?.addEventListener("change", () => {
      if (this.tool) this.tool.contrast = Math.max(1, Math.min(100, parseInt(contrastInput.value) || 50));
    });
    const frequencyInput = this.el.querySelector<HTMLInputElement>(".maglasso-frequency-input");
    frequencyInput?.addEventListener("change", () => {
      if (this.tool) this.tool.frequency = Math.max(0, Math.min(100, parseInt(frequencyInput.value) || 50));
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Quick Selection Tool Options (#189) ─────────────────────
//
// Brush Size wired to tool.brushSize. Sample All Layers wired to
// tool.sampleAllLayers. Auto-Enhance does not exist on QuickSelectionTool
// — wired via dynamic assignment (render consumer pending).

export class QuickSelectionToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const brushSize: number = tool?.brushSize ?? 20;
    const sampleAllLayers: boolean = tool?.sampleAllLayers === true;
    const autoEnhance: boolean = tool?.autoEnhance === true;
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Size: <input type="range" min="1" max="500" value="${brushSize}" class="qs-brushsize-input"> <span class="qs-brushsize-label">${brushSize}</span></label>
      <label class="opt"><input type="checkbox" class="qs-samplealllayers-input" ${sampleAllLayers ? "checked" : ""}> Sample All Layers</label>
      <label class="opt"><input type="checkbox" class="qs-autoenhance-input" ${autoEnhance ? "checked" : ""}> Auto-Enhance</label>
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    const sizeInput = this.el.querySelector<HTMLInputElement>(".qs-brushsize-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".qs-brushsize-label");
    sizeInput?.addEventListener("input", () => {
      const v = parseInt(sizeInput.value) || 20;
      if (this.tool) this.tool.brushSize = v;
      if (sizeLabel) sizeLabel.textContent = String(v);
    });
    const sampleAllInput = this.el.querySelector<HTMLInputElement>(".qs-samplealllayers-input");
    sampleAllInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllInput.checked;
    });
    const autoEnhanceInput = this.el.querySelector<HTMLInputElement>(".qs-autoenhance-input");
    autoEnhanceInput?.addEventListener("change", () => {
      if (this.tool) this.tool.autoEnhance = autoEnhanceInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Eyedropper Tool Options ─────────────────────────────────

export class EyedropperToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const sampleSize = tool?.sampleSize ?? "point";
    const sampleAllLayers = tool?.sampleAllLayers === true;
    const showSamplingRing = tool?.showSamplingRing !== false;
    this.el.innerHTML = `
      <label class="opt">Sample Size:
        <select class="eyedropper-samplesize-input">
          <option value="point" ${sampleSize === "point" ? "selected" : ""}>Point Sample</option>
          <option value="3x3" ${sampleSize === "3x3" ? "selected" : ""}>3×3 Average</option>
          <option value="5x5" ${sampleSize === "5x5" ? "selected" : ""}>5×5 Average</option>
          <option value="11x11" ${sampleSize === "11x11" ? "selected" : ""}>11×11 Average</option>
          <option value="31x31" ${sampleSize === "31x31" ? "selected" : ""}>31×31 Average</option>
          <option value="51x51" ${sampleSize === "51x51" ? "selected" : ""}>51×51 Average</option>
          <option value="101x101" ${sampleSize === "101x101" ? "selected" : ""}>101×101 Average</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="eyedropper-samplealllayers-input"> Sample All Layers</label>
      <label class="opt"><input type="checkbox" ${showSamplingRing ? "checked" : ""} class="eyedropper-showsamplingring-input"> Show Sampling Ring</label>
    `;
    const sampleSizeInput = this.el.querySelector<HTMLSelectElement>(".eyedropper-samplesize-input");
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".eyedropper-samplealllayers-input");
    const showSamplingRingInput = this.el.querySelector<HTMLInputElement>(".eyedropper-showsamplingring-input");

    sampleSizeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleSize = sampleSizeInput.value as any;
    });
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
    showSamplingRingInput?.addEventListener("change", () => {
      if (this.tool) this.tool.showSamplingRing = showSamplingRingInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Pencil Tool Options (#70) ───────────────────────────────
//
// Like Brush, minus Flow and Airbrush (Photoshop's Pencil has neither).
// PencilTool previously only exposed `size` — `opacity` and `blendMode` are
// new fields added directly to PencilTool so these controls are real
// (opacity is wired into the tool's paint stamps; blendMode is stored for
// forward-compat, matching Brush's own not-yet-consumed Mode dropdown).

export class PencilToolOptions implements ToolOptions {
  private tool: any;
  private onChange: () => void;
  private el: HTMLElement;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const size = tool?.size ?? 1;
    const opacityPct = Math.round((tool?.opacity ?? 1) * 100);
    const blendMode = tool?.blendMode ?? "normal";
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="pencil-blendmode-input">
          ${buildPaintingBlendModeOptions(blendMode)}
        </select>
      </label>
      <label class="opt">Opacity:
        <input type="range" min="0" max="100" value="${opacityPct}" class="pencil-opacity-input" style="width:80px">
        <input type="number" min="0" max="100" value="${opacityPct}" class="pencil-opacity-num" style="width:46px">%
      </label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${size}" class="pencil-size-input"> <span class="pencil-size-label">${size}</span></label>
    `;
    const blendModeSelect = this.el.querySelector<HTMLSelectElement>(".pencil-blendmode-input");
    blendModeSelect?.addEventListener("change", () => {
      if (this.tool) this.tool.blendMode = blendModeSelect.value;
    });
    this.bindOpacity();
    const sizeInput = this.el.querySelector<HTMLInputElement>(".pencil-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".pencil-size-label");
    sizeInput?.addEventListener("input", () => {
      if (this.tool) this.tool.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
      this.onChange();
    });
  }

  private bindOpacity(): void {
    const range = this.el.querySelector<HTMLInputElement>(".pencil-opacity-input");
    const num = this.el.querySelector<HTMLInputElement>(".pencil-opacity-num");
    if (!range || !num) return;
    const apply = (raw: string): void => {
      const n = Math.max(0, Math.min(100, parseFloat(raw) || 0));
      range.value = String(n);
      num.value = String(n);
      if (this.tool) this.tool.opacity = n / 100;
      this.onChange();
    };
    range.addEventListener("input", () => apply(range.value));
    num.addEventListener("input", () => apply(num.value));
    num.addEventListener("change", () => apply(num.value));
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}

  /**
   * audit i8: `[`/`]` mutate `pencilTool.size` directly from main.ts's
   * keydown handler (adjustBrushSize), bypassing this options bar entirely.
   * Re-read the tool's current fields and push them into the existing
   * controls without rebuilding the DOM — mirrors BrushToolOptions.refresh().
   */
  refresh(): void {
    if (!this.tool) return;
    const sizeInput = this.el.querySelector<HTMLInputElement>(".pencil-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".pencil-size-label");
    const size = this.tool.size ?? 1;
    if (sizeInput) sizeInput.value = String(size);
    if (sizeLabel) sizeLabel.textContent = String(size);

    const opacityPct = Math.round((this.tool.opacity ?? 1) * 100);
    const range = this.el.querySelector<HTMLInputElement>(".pencil-opacity-input");
    const num = this.el.querySelector<HTMLInputElement>(".pencil-opacity-num");
    if (range) range.value = String(opacityPct);
    if (num) num.value = String(opacityPct);

    const blendModeSelect = this.el.querySelector<HTMLSelectElement>(".pencil-blendmode-input");
    if (blendModeSelect) blendModeSelect.value = this.tool.blendMode ?? "normal";
  }
}

// ── Eraser Tool Options (#75) ───────────────────────────────
//
// Eraser previously reused BrushToolOptions verbatim, which shows the full
// 29-entry painting blend-mode list — a dead control for an erase tool
// (Photoshop's Eraser options bar has no blend-mode dropdown at all). This
// gives Eraser its own options bar with a Brush/Pencil/Block tip-mode
// selector instead, matching Photoshop's real Eraser options bar.
//
// `eraserType` and `eraseToHistory` are dynamic UI-only fields on the
// shared BrushTool instance — not yet consumed by the paint/erase pixel
// path (which always erases to transparency with a round soft-edged tip
// regardless of these settings). Tracked as a follow-up.

export class EraserToolOptions implements ToolOptions {
  private tool: any;
  private onChange: () => void;
  private el: HTMLElement;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, opacity: 1, flow: 1 };
    const opacityPct = Math.round((s.opacity ?? 1) * 100);
    const flowPct = Math.round((s.flow ?? 1) * 100);
    const eraserType = tool?.eraserType ?? "brush";
    const eraseToHistory = tool?.eraseToHistory === true;
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="eraser-tipmode-input">
          <option value="brush" ${eraserType === "brush" ? "selected" : ""}>Brush</option>
          <option value="pencil" ${eraserType === "pencil" ? "selected" : ""}>Pencil</option>
          <option value="block" ${eraserType === "block" ? "selected" : ""}>Block</option>
        </select>
      </label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="eraser-size-input"> <span class="eraser-size-label">${s.size}</span></label>
      <label class="opt">Opacity:
        <input type="range" min="0" max="100" value="${opacityPct}" class="eraser-opacity-input" style="width:80px">
        <input type="number" min="0" max="100" value="${opacityPct}" class="eraser-opacity-num" style="width:46px">%
      </label>
      <label class="opt">Flow:
        <input type="range" min="0" max="100" value="${flowPct}" class="eraser-flow-input" style="width:80px">
        <input type="number" min="0" max="100" value="${flowPct}" class="eraser-flow-num" style="width:46px">%
      </label>
      <label class="opt"><input type="checkbox" ${eraseToHistory ? "checked" : ""} class="eraser-erasetohistory-input"> Erase to History</label>
    `;
    const tipModeSelect = this.el.querySelector<HTMLSelectElement>(".eraser-tipmode-input");
    tipModeSelect?.addEventListener("change", () => {
      if (this.tool) this.tool.eraserType = tipModeSelect.value;
    });
    const sizeInput = this.el.querySelector<HTMLInputElement>(".eraser-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".eraser-size-label");
    sizeInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
      this.onChange();
    });
    this.bindPair(".eraser-opacity-input", ".eraser-opacity-num", "opacity");
    this.bindPair(".eraser-flow-input", ".eraser-flow-num", "flow");
    const eraseToHistoryInput = this.el.querySelector<HTMLInputElement>(".eraser-erasetohistory-input");
    eraseToHistoryInput?.addEventListener("change", () => {
      if (this.tool) this.tool.eraseToHistory = eraseToHistoryInput.checked;
    });
  }

  private bindPair(rangeSel: string, numSel: string, key: string): void {
    const range = this.el.querySelector<HTMLInputElement>(rangeSel);
    const num = this.el.querySelector<HTMLInputElement>(numSel);
    if (!range || !num) return;
    const apply = (raw: string): void => {
      const n = Math.max(0, Math.min(100, parseFloat(raw) || 0));
      range.value = String(n);
      num.value = String(n);
      if (this.tool?.settings) this.tool.settings[key] = n / 100;
      this.onChange();
    };
    range.addEventListener("input", () => apply(range.value));
    num.addEventListener("input", () => apply(num.value));
    num.addEventListener("change", () => apply(num.value));
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Object Selection Tool Options (#63) ─────────────────────
//
// `mode` (rectangle/lasso) and `sampleAllLayers` are dynamic fields —
// ObjectSelectionTool currently always drags a rectangular hint region and
// always samples only the active layer, regardless of these settings.
// Wiring an actual lasso-shaped hint region and multi-layer compositing is
// tracked as a follow-up; this makes the controls read/write correctly in
// the meantime (same "pending consumer" pattern as PenToolOptions's
// Auto Add/Delete / Rubber Band checkboxes above).

export class ObjectSelectionToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const mode = tool?.mode ?? "rectangle";
    const sampleAllLayers = tool?.sampleAllLayers === true;
    this.el.innerHTML = `
      ${buildCombineModeButtonsHtml(tool?.combineMode ?? "new")}
      <label class="opt">Mode:
        <select class="objsel-mode-input">
          <option value="rectangle" ${mode === "rectangle" ? "selected" : ""}>Rectangle</option>
          <option value="lasso" ${mode === "lasso" ? "selected" : ""}>Lasso</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="objsel-samplealllayers-input"> Sample All Layers</label>
      ${buildSelectAndMaskButtonHtml()}
    `;
    wireCombineModeButtons(this.el, this.tool);
    wireSelectAndMaskButton(this.el);
    const modeInput = this.el.querySelector<HTMLSelectElement>(".objsel-mode-input");
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value;
    });
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".objsel-samplealllayers-input");
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Healing Brush Tool Options ──────────────────────────────

export class HealingBrushToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 20, hardness: 0.5, opacity: 1, flow: 1 };
    const aligned = tool?.aligned !== false;
    const sampleMode = tool?.sampleMode ?? "current";
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="heal-size-input"> <span class="heal-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="heal-hardness-input"> <span class="heal-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 1) * 100)}" class="heal-opacity-input"> <span class="heal-opacity-label">${Math.round((s.opacity ?? 1) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 1) * 100)}" class="heal-flow-input"> <span class="heal-flow-label">${Math.round((s.flow ?? 1) * 100)}%</span></label>
      <label class="opt"><input type="checkbox" ${aligned ? "checked" : ""} class="heal-aligned-input"> Aligned</label>
      <label class="opt">Sample:
        <select class="heal-sample-input">
          <option value="current" ${sampleMode === "current" ? "selected" : ""}>Current Layer</option>
          <option value="below" ${sampleMode === "below" ? "selected" : ""}>Current & Below</option>
          <option value="all" ${sampleMode === "all" ? "selected" : ""}>All Layers</option>
        </select>
      </label>
    `;
    this.bindSlider(".heal-size-input", ".heal-size-label", "size", 1, false);
    this.bindSlider(".heal-hardness-input", ".heal-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".heal-opacity-input", ".heal-opacity-label", "opacity", 0.01, true);
    this.bindSlider(".heal-flow-input", ".heal-flow-label", "flow", 0.01, true);
    const alignedInput = this.el.querySelector<HTMLInputElement>(".heal-aligned-input");
    alignedInput?.addEventListener("change", () => {
      if (this.tool) this.tool.aligned = alignedInput.checked;
    });
    const sampleInput = this.el.querySelector<HTMLSelectElement>(".heal-sample-input");
    sampleInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleMode = sampleInput.value;
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Spot Healing Brush Tool Options ─────────────────────────

export class SpotHealingToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 20, hardness: 0.5, opacity: 1, flow: 1 };
    const sampleMode = tool?.sampleMode ?? "current";
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="spotheal-size-input"> <span class="spotheal-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="spotheal-hardness-input"> <span class="spotheal-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 1) * 100)}" class="spotheal-opacity-input"> <span class="spotheal-opacity-label">${Math.round((s.opacity ?? 1) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 1) * 100)}" class="spotheal-flow-input"> <span class="spotheal-flow-label">${Math.round((s.flow ?? 1) * 100)}%</span></label>
      <label class="opt">Sample:
        <select class="spotheal-sample-input">
          <option value="current" ${sampleMode === "current" ? "selected" : ""}>Current Layer</option>
          <option value="below" ${sampleMode === "below" ? "selected" : ""}>Current & Below</option>
          <option value="all" ${sampleMode === "all" ? "selected" : ""}>All Layers</option>
        </select>
      </label>
    `;
    this.bindSlider(".spotheal-size-input", ".spotheal-size-label", "size", 1, false);
    this.bindSlider(".spotheal-hardness-input", ".spotheal-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".spotheal-opacity-input", ".spotheal-opacity-label", "opacity", 0.01, true);
    this.bindSlider(".spotheal-flow-input", ".spotheal-flow-label", "flow", 0.01, true);
    const sampleInput = this.el.querySelector<HTMLSelectElement>(".spotheal-sample-input");
    sampleInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleMode = sampleInput.value;
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Patch Tool Options ──────────────────────────────────────
//
// PatchTool has no configurable settings today (only internal drag state).
// `patchMode` and `sampleAllLayers` are dynamic UI-only fields — not yet
// consumed by the tool's blend logic, which always patches using the
// current layer only. Tracked as a follow-up (same pattern as other
// pending-consumer fields in this file).

export class PatchToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const patchMode = tool?.patchMode ?? "source";
    const sampleAllLayers = tool?.sampleAllLayers === true;
    this.el.innerHTML = `
      <label class="opt">Patch:
        <select class="patch-mode-input">
          <option value="source" ${patchMode === "source" ? "selected" : ""}>Source</option>
          <option value="destination" ${patchMode === "destination" ? "selected" : ""}>Destination</option>
        </select>
      </label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="patch-samplealllayers-input"> Sample All Layers</label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".patch-mode-input");
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.patchMode = modeInput.value;
    });
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".patch-samplealllayers-input");
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Red Eye Tool Options ────────────────────────────────────

export class RedEyeToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const size = tool?.size ?? 20;
    const darkness = tool?.pupilDarkness ?? 50;
    this.el.innerHTML = `
      <label class="opt">Pupil Size: <input type="range" min="1" max="500" value="${size}" class="redeye-size-input"> <span class="redeye-size-label">${size}</span></label>
      <label class="opt">Darken Amount: <input type="range" min="0" max="100" value="${darkness}" class="redeye-darkness-input"> <span class="redeye-darkness-label">${darkness}%</span></label>
    `;
    const sizeInput = this.el.querySelector<HTMLInputElement>(".redeye-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".redeye-size-label");
    sizeInput?.addEventListener("input", () => {
      if (this.tool) this.tool.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
      this.onChange();
    });
    const darknessInput = this.el.querySelector<HTMLInputElement>(".redeye-darkness-input");
    const darknessLabel = this.el.querySelector<HTMLSpanElement>(".redeye-darkness-label");
    darknessInput?.addEventListener("input", () => {
      if (this.tool) this.tool.pupilDarkness = parseInt(darknessInput.value) || 0;
      if (darknessLabel) darknessLabel.textContent = `${darknessInput.value}%`;
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Mixer Brush Tool Options ─────────────────────────────────

export class MixerBrushToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 0.5, wet: 0.5, load: 0.5, mix: 0.5 };
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="mixer-size-input"> <span class="mixer-size-label">${s.size}</span></label>
      <label class="opt">Wet: <input type="range" min="0" max="100" value="${Math.round((s.wet ?? 0.5) * 100)}" class="mixer-wet-input"> <span class="mixer-wet-label">${Math.round((s.wet ?? 0.5) * 100)}%</span></label>
      <label class="opt">Load: <input type="range" min="0" max="100" value="${Math.round((s.load ?? 0.5) * 100)}" class="mixer-load-input"> <span class="mixer-load-label">${Math.round((s.load ?? 0.5) * 100)}%</span></label>
      <label class="opt">Mix: <input type="range" min="0" max="100" value="${Math.round((s.mix ?? 0.5) * 100)}" class="mixer-mix-input"> <span class="mixer-mix-label">${Math.round((s.mix ?? 0.5) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 0.5) * 100)}" class="mixer-flow-input"> <span class="mixer-flow-label">${Math.round((s.flow ?? 0.5) * 100)}%</span></label>
    `;
    this.bindSlider(".mixer-size-input", ".mixer-size-label", "size", 1, false);
    this.bindSlider(".mixer-wet-input", ".mixer-wet-label", "wet", 0.01, true);
    this.bindSlider(".mixer-load-input", ".mixer-load-label", "load", 0.01, true);
    this.bindSlider(".mixer-mix-input", ".mixer-mix-label", "mix", 0.01, true);
    this.bindSlider(".mixer-flow-input", ".mixer-flow-label", "flow", 0.01, true);
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Pattern Stamp Tool Options ───────────────────────────────
//
// Unlike Clone/Healing Brush, PatternStampTool's brush fields (size,
// hardness, opacity, flow) are top-level, not nested under `.settings`.

export class PatternStampToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const size = tool?.size ?? 30;
    const hardness = tool?.hardness ?? 0.5;
    const opacity = tool?.opacity ?? 1;
    const flow = tool?.flow ?? 1;
    const aligned = tool?.aligned === true;
    this.el.innerHTML = `
      <span class="patstamp-preview-wrap" style="position:relative;display:inline-flex;align-items:center;gap:4px;">
        <canvas class="patstamp-swatch" width="24" height="24" style="width:24px;height:24px;border:1px solid rgba(128,128,128,0.6);image-rendering:pixelated;display:none;" title="Current pattern"></canvas>
        <button type="button" class="patstamp-change-btn opt-btn" title="Choose a pattern">Change Pattern...</button>
      </span>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${size}" class="patstamp-size-input"> <span class="patstamp-size-label">${size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round(hardness * 100)}" class="patstamp-hardness-input"> <span class="patstamp-hardness-label">${Math.round(hardness * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round(opacity * 100)}" class="patstamp-opacity-input"> <span class="patstamp-opacity-label">${Math.round(opacity * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round(flow * 100)}" class="patstamp-flow-input"> <span class="patstamp-flow-label">${Math.round(flow * 100)}%</span></label>
      <label class="opt"><input type="checkbox" ${aligned ? "checked" : ""} class="patstamp-aligned-input"> Aligned</label>
    `;
    this.bindSlider(".patstamp-size-input", ".patstamp-size-label", "size", 1, false);
    this.bindSlider(".patstamp-hardness-input", ".patstamp-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".patstamp-opacity-input", ".patstamp-opacity-label", "opacity", 0.01, true);
    this.bindSlider(".patstamp-flow-input", ".patstamp-flow-label", "flow", 0.01, true);
    const alignedInput = this.el.querySelector<HTMLInputElement>(".patstamp-aligned-input");
    alignedInput?.addEventListener("change", () => {
      if (this.tool) this.tool.aligned = alignedInput.checked;
    });

    // i184: PatternStampTool exposes `hasPattern`/`getPatternPreviewThumbnail()`
    // so the options bar can show which pattern is loaded — render that here.
    this.updateSwatch();

    // i184: the actual pattern picker (main.ts's openPatternEditor()) is a
    // module-scoped function in main.ts, not reachable from ToolUX — main.ts
    // needs to add a listener for this bubbling event (same not-yet-wired
    // convention as BrushToolOptions's `.brush-preset-btn`
    // (pixelpa:open-brush-presets) and TypeToolOptions's
    // pixelpa:type-commit/pixelpa:type-cancel) that calls openPatternEditor()
    // and, once the user picks a pattern, sets `patternStampTool.pattern` and
    // calls `toolUx.refreshOptions()` so refresh() below picks up the change.
    const changeBtn = this.el.querySelector<HTMLButtonElement>(".patstamp-change-btn");
    changeBtn?.addEventListener("click", () => {
      const evt = new CustomEvent("pixelpa:open-pattern-editor", { bubbles: true });
      this.el.dispatchEvent(evt);
    });
  }

  /**
   * Re-read `tool.hasPattern`/`getPatternPreviewThumbnail()` into the swatch
   * without rebuilding the DOM (mirrors the ToolOptions.refresh() contract
   * used elsewhere in this file, e.g. ShapeToolOptions.refresh()). Lets a
   * future main.ts pattern-picker wiring call `toolUx.refreshOptions()` after
   * loading a new pattern and have the swatch stay in sync.
   */
  refresh(): void {
    this.updateSwatch();
  }

  private updateSwatch(): void {
    const canvas = this.el.querySelector<HTMLCanvasElement>(".patstamp-swatch");
    if (!canvas) return;
    const hasPattern = this.tool?.hasPattern === true;
    canvas.style.display = hasPattern ? "" : "none";
    if (!hasPattern || typeof this.tool?.getPatternPreviewThumbnail !== "function") return;
    const thumb = this.tool.getPatternPreviewThumbnail(24);
    if (!thumb) return;
    // jsdom (and other headless test environments) do not implement a real
    // 2D canvas backend — getContext() returns null there. Skip drawing
    // rather than throwing; the swatch is a visual nicety, not load-bearing.
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = thumb.width;
    canvas.height = thumb.height;
    const pixels = new Uint8ClampedArray(thumb.pixels);
    // Real browsers always have a global ImageData constructor; jsdom (used
    // in tests, with getContext() mocked to a non-null stub) does not, so
    // fall back to a plain object of the same shape in that case.
    const imageData = typeof ImageData !== "undefined"
      ? new ImageData(pixels, thumb.width, thumb.height)
      : ({ data: pixels, width: thumb.width, height: thumb.height } as ImageData);
    ctx.putImageData(imageData, 0, 0);
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool) this.tool[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Color Replacement Tool Options ──────────────────────────

export class ColorReplacementToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 1, mode: "continuous", limits: "contiguous", tolerance: 32 };
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="colrep-size-input"> <span class="colrep-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="colrep-hardness-input"> <span class="colrep-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Sampling:
        <select class="colrep-sampling-input">
          <option value="continuous" ${s.mode === "continuous" ? "selected" : ""}>Continuous</option>
          <option value="once" ${s.mode === "once" ? "selected" : ""}>Once</option>
          <option value="background-swatch" ${s.mode === "background-swatch" ? "selected" : ""}>Background Swatch</option>
        </select>
      </label>
      <label class="opt">Limits:
        <select class="colrep-limits-input">
          <option value="discontiguous" ${s.limits === "discontiguous" ? "selected" : ""}>Discontiguous</option>
          <option value="contiguous" ${s.limits === "contiguous" ? "selected" : ""}>Contiguous</option>
        </select>
      </label>
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${s.tolerance}" class="colrep-tolerance-input" style="width:50px"></label>
    `;
    this.bindSlider(".colrep-size-input", ".colrep-size-label", "size", 1, false);
    this.bindSlider(".colrep-hardness-input", ".colrep-hardness-label", "hardness", 0.01, true);
    const samplingInput = this.el.querySelector<HTMLSelectElement>(".colrep-sampling-input");
    samplingInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.mode = samplingInput.value;
    });
    const limitsInput = this.el.querySelector<HTMLSelectElement>(".colrep-limits-input");
    limitsInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.limits = limitsInput.value;
    });
    const toleranceInput = this.el.querySelector<HTMLInputElement>(".colrep-tolerance-input");
    toleranceInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.tolerance = Math.max(0, Math.min(255, parseInt(toleranceInput.value) || 0));
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Background Eraser Tool Options ──────────────────────────

export class BackgroundEraserToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 50, hardness: 0.5, opacity: 1, flow: 1, tolerance: 32, sampling: "continuous", limits: "discontiguous", protectForeground: false };
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="bgeraser-size-input"> <span class="bgeraser-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="bgeraser-hardness-input"> <span class="bgeraser-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Limits:
        <select class="bgeraser-limits-input">
          <option value="discontiguous" ${s.limits === "discontiguous" ? "selected" : ""}>Discontiguous</option>
          <option value="contiguous" ${s.limits === "contiguous" ? "selected" : ""}>Contiguous</option>
          <option value="find-edges" ${s.limits === "find-edges" ? "selected" : ""}>Find Edges</option>
        </select>
      </label>
      <label class="opt">Sampling:
        <select class="bgeraser-sampling-input">
          <option value="continuous" ${s.sampling === "continuous" ? "selected" : ""}>Continuous</option>
          <option value="once" ${s.sampling === "once" ? "selected" : ""}>Once</option>
          <option value="background-swatch" ${s.sampling === "background-swatch" ? "selected" : ""}>Background Swatch</option>
        </select>
      </label>
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${s.tolerance}" class="bgeraser-tolerance-input" style="width:50px"></label>
      <label class="opt"><input type="checkbox" ${s.protectForeground ? "checked" : ""} class="bgeraser-protectfg-input"> Protect Foreground Color</label>
    `;
    this.bindSlider(".bgeraser-size-input", ".bgeraser-size-label", "size", 1, false);
    this.bindSlider(".bgeraser-hardness-input", ".bgeraser-hardness-label", "hardness", 0.01, true);
    const limitsInput = this.el.querySelector<HTMLSelectElement>(".bgeraser-limits-input");
    limitsInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.limits = limitsInput.value;
    });
    const samplingInput = this.el.querySelector<HTMLSelectElement>(".bgeraser-sampling-input");
    samplingInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.sampling = samplingInput.value;
    });
    const toleranceInput = this.el.querySelector<HTMLInputElement>(".bgeraser-tolerance-input");
    toleranceInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.tolerance = Math.max(0, Math.min(255, parseInt(toleranceInput.value) || 0));
    });
    // audit i83 UI: BackgroundEraserTool.settings.protectForeground already
    // works (preserves pixels matching the foreground color while erasing)
    // but had no options-bar control.
    const protectFgInput = this.el.querySelector<HTMLInputElement>(".bgeraser-protectfg-input");
    protectFgInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.protectForeground = protectFgInput.checked;
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Magic Eraser Tool Options ────────────────────────────────
//
// Mirrors MagicWandToolOptions — same underlying selection algorithm, but
// the result is deleted (erased) instead of turned into a selection.

export class MagicEraserToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { tolerance: 32, antiAlias: true, contiguous: true, sampleAllLayers: false, opacity: 1 };
    this.el.innerHTML = `
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${s.tolerance}" class="meraser-tolerance-input" style="width:50px"></label>
      <label class="opt"><input type="checkbox" ${s.antiAlias !== false ? "checked" : ""} class="meraser-antialias-input"> Anti-alias</label>
      <label class="opt"><input type="checkbox" ${s.contiguous !== false ? "checked" : ""} class="meraser-contiguous-input"> Contiguous</label>
      <label class="opt"><input type="checkbox" ${s.sampleAllLayers === true ? "checked" : ""} class="meraser-samplealllayers-input"> Sample All Layers</label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 1) * 100)}" class="meraser-opacity-input"> <span class="meraser-opacity-label">${Math.round((s.opacity ?? 1) * 100)}%</span></label>
    `;
    const tolInput = this.el.querySelector<HTMLInputElement>(".meraser-tolerance-input");
    tolInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.tolerance = Math.max(0, Math.min(255, parseInt(tolInput.value) || 0));
    });
    const aaInput = this.el.querySelector<HTMLInputElement>(".meraser-antialias-input");
    aaInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.antiAlias = aaInput.checked;
    });
    const contInput = this.el.querySelector<HTMLInputElement>(".meraser-contiguous-input");
    contInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.contiguous = contInput.checked;
    });
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".meraser-samplealllayers-input");
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.sampleAllLayers = sampleAllLayersInput.checked;
    });
    const opacityInput = this.el.querySelector<HTMLInputElement>(".meraser-opacity-input");
    const opacityLabel = this.el.querySelector<HTMLSpanElement>(".meraser-opacity-label");
    opacityInput?.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings.opacity = parseInt(opacityInput.value) / 100;
      if (opacityLabel) opacityLabel.textContent = `${opacityInput.value}%`;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Content-Aware Move Tool Options ─────────────────────────

export class ContentAwareMoveToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const mode = tool?.mode ?? "move";
    const adaptation = tool?.adaptation ?? 5;
    const sampleAllLayers = tool?.sampleAllLayers === true;
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="camove-mode-input">
          <option value="move" ${mode === "move" ? "selected" : ""}>Move</option>
          <option value="extend" ${mode === "extend" ? "selected" : ""}>Extend</option>
        </select>
      </label>
      <label class="opt">Adaptation: <input type="number" min="1" max="7" value="${adaptation}" class="camove-adaptation-input" style="width:44px"></label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="camove-samplealllayers-input"> Sample All Layers</label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".camove-mode-input");
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value;
    });
    const adaptationInput = this.el.querySelector<HTMLInputElement>(".camove-adaptation-input");
    adaptationInput?.addEventListener("change", () => {
      if (this.tool) this.tool.adaptation = Math.max(1, Math.min(7, parseInt(adaptationInput.value) || 5));
    });
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".camove-samplealllayers-input");
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Remove Tool Options ─────────────────────────────────────

export class RemoveToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const size = tool?.size ?? 50;
    const hardness = tool?.hardness ?? 0.5;
    const sampleAllLayers = tool?.sampleAllLayers === true;
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${size}" class="remove-size-input"> <span class="remove-size-label">${size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round(hardness * 100)}" class="remove-hardness-input"> <span class="remove-hardness-label">${Math.round(hardness * 100)}%</span></label>
      <label class="opt"><input type="checkbox" ${sampleAllLayers ? "checked" : ""} class="remove-samplealllayers-input"> Sample All Layers</label>
    `;
    const sizeInput = this.el.querySelector<HTMLInputElement>(".remove-size-input");
    const sizeLabel = this.el.querySelector<HTMLSpanElement>(".remove-size-label");
    sizeInput?.addEventListener("input", () => {
      if (this.tool) this.tool.size = parseInt(sizeInput.value) || 1;
      if (sizeLabel) sizeLabel.textContent = sizeInput.value;
      this.onChange();
    });
    const hardnessInput = this.el.querySelector<HTMLInputElement>(".remove-hardness-input");
    const hardnessLabel = this.el.querySelector<HTMLSpanElement>(".remove-hardness-label");
    hardnessInput?.addEventListener("input", () => {
      if (this.tool) this.tool.hardness = (parseInt(hardnessInput.value) || 0) / 100;
      if (hardnessLabel) hardnessLabel.textContent = `${hardnessInput.value}%`;
      this.onChange();
    });
    const sampleAllLayersInput = this.el.querySelector<HTMLInputElement>(".remove-samplealllayers-input");
    sampleAllLayersInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleAllLayers = sampleAllLayersInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Art History Brush Tool Options ──────────────────────────

const ART_HISTORY_STYLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "tight-short", label: "Tight Short" },
  { value: "tight-medium", label: "Tight Medium" },
  { value: "tight-long", label: "Tight Long" },
  { value: "loose-medium", label: "Loose Medium" },
  { value: "loose-long", label: "Loose Long" },
  { value: "dab", label: "Dab" },
  { value: "tight-curl", label: "Tight Curl" },
  { value: "loose-curl", label: "Loose Curl" },
];

export class ArtHistoryBrushToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const s = tool?.settings ?? { size: 30, opacity: 0.5, style: "loose-medium", area: 100, tolerance: 0 };
    this.el.innerHTML = `
      <label class="opt">Style:
        <select class="arthist-style-input">
          ${ART_HISTORY_STYLES.map((o) => `<option value="${o.value}" ${s.style === o.value ? "selected" : ""}>${o.label}</option>`).join("")}
        </select>
      </label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="arthist-size-input"> <span class="arthist-size-label">${s.size}</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round((s.opacity ?? 0.5) * 100)}" class="arthist-opacity-input"> <span class="arthist-opacity-label">${Math.round((s.opacity ?? 0.5) * 100)}%</span></label>
      <label class="opt">Area: <input type="number" min="0" max="500" value="${s.area}" class="arthist-area-input" style="width:50px"></label>
      <label class="opt">Tolerance: <input type="number" min="0" max="255" value="${s.tolerance}" class="arthist-tolerance-input" style="width:50px"></label>
    `;
    const styleInput = this.el.querySelector<HTMLSelectElement>(".arthist-style-input");
    styleInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.style = styleInput.value;
    });
    this.bindSlider(".arthist-size-input", ".arthist-size-label", "size", 1, false);
    this.bindSlider(".arthist-opacity-input", ".arthist-opacity-label", "opacity", 0.01, true);
    const areaInput = this.el.querySelector<HTMLInputElement>(".arthist-area-input");
    areaInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.area = Math.max(0, parseInt(areaInput.value) || 0);
    });
    const toleranceInput = this.el.querySelector<HTMLInputElement>(".arthist-tolerance-input");
    toleranceInput?.addEventListener("change", () => {
      if (this.tool?.settings) this.tool.settings.tolerance = Math.max(0, Math.min(255, parseInt(toleranceInput.value) || 0));
    });
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Sponge Tool Options ─────────────────────────────────────

export class SpongeToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const mode = tool?.mode ?? "saturate";
    const s = tool?.settings ?? { size: 30, hardness: 0.5, opacity: 1, flow: 0.5 };
    this.el.innerHTML = `
      <label class="opt">Mode:
        <select class="sponge-mode-input">
          <option value="desaturate" ${mode === "desaturate" ? "selected" : ""}>Desaturate</option>
          <option value="saturate" ${mode === "saturate" ? "selected" : ""}>Saturate</option>
        </select>
      </label>
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${s.size}" class="sponge-size-input"> <span class="sponge-size-label">${s.size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round((s.hardness ?? 0.5) * 100)}" class="sponge-hardness-input"> <span class="sponge-hardness-label">${Math.round((s.hardness ?? 0.5) * 100)}%</span></label>
      <label class="opt">Flow: <input type="range" min="0" max="100" value="${Math.round((s.flow ?? 0.5) * 100)}" class="sponge-flow-input"> <span class="sponge-flow-label">${Math.round((s.flow ?? 0.5) * 100)}%</span></label>
    `;
    const modeInput = this.el.querySelector<HTMLSelectElement>(".sponge-mode-input");
    modeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.mode = modeInput.value;
    });
    this.bindSlider(".sponge-size-input", ".sponge-size-label", "size", 1, false);
    this.bindSlider(".sponge-hardness-input", ".sponge-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".sponge-flow-input", ".sponge-flow-label", "flow", 0.01, true);
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool?.settings) this.tool.settings[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Color Sampler Tool Options ──────────────────────────────
//
// `sampleSize` is a dynamic UI-only field — ColorSamplerTool.sampleAt()
// always does a point sample regardless of this setting (averaging is not
// yet implemented). "Clear" calls the tool's real clearAll() method.

export class ColorSamplerToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const sampleSize = tool?.sampleSize ?? "point";
    const count = tool?.samplers?.length ?? 0;
    this.el.innerHTML = `
      <label class="opt">Sample Size:
        <select class="colorsampler-samplesize-input">
          <option value="point" ${sampleSize === "point" ? "selected" : ""}>Point Sample</option>
          <option value="3x3" ${sampleSize === "3x3" ? "selected" : ""}>3×3 Average</option>
          <option value="5x5" ${sampleSize === "5x5" ? "selected" : ""}>5×5 Average</option>
          <option value="11x11" ${sampleSize === "11x11" ? "selected" : ""}>11×11 Average</option>
        </select>
      </label>
      <span class="opt colorsampler-count">${count} sampler(s)</span>
      <button type="button" class="colorsampler-clear-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Clear</button>
    `;
    const sampleSizeInput = this.el.querySelector<HTMLSelectElement>(".colorsampler-samplesize-input");
    sampleSizeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.sampleSize = sampleSizeInput.value;
    });
    const clearBtn = this.el.querySelector<HTMLButtonElement>(".colorsampler-clear-btn");
    clearBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.clearAll === "function") this.tool.clearAll();
      const countEl = this.el.querySelector<HTMLSpanElement>(".colorsampler-count");
      if (countEl) countEl.textContent = "0 sampler(s)";
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Ruler Tool Options ──────────────────────────────────────
//
// Read-only measurement readout (X/Y/X2/Y2/distance/angle) sourced from
// RulerTool.measurement, plus a Clear button wired to the real clear().

export class RulerToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const m = tool?.measurement ?? null;
    this.el.innerHTML = `
      <span class="opt ruler-readout">
        X1: <span class="ruler-x1">${m ? m.x1 : "—"}</span>
        Y1: <span class="ruler-y1">${m ? m.y1 : "—"}</span>
        X2: <span class="ruler-x2">${m ? m.x2 : "—"}</span>
        Y2: <span class="ruler-y2">${m ? m.y2 : "—"}</span>
        D: <span class="ruler-distance">${m ? m.distance : "—"}</span>
        A: <span class="ruler-angle">${m ? m.angle : "—"}</span>&deg;
      </span>
      <button type="button" class="ruler-clear-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Clear</button>
    `;
    const clearBtn = this.el.querySelector<HTMLButtonElement>(".ruler-clear-btn");
    clearBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.clear === "function") this.tool.clear();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Note Tool Options ───────────────────────────────────────
//
// `author` and `defaultColor` are dynamic UI-only fields — NoteTool always
// creates notes with a hardcoded "#ffffcc" color and no author metadata
// today. "Clear All" calls the tool's real clearAll() method.

export class NoteToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const author = tool?.author ?? "";
    const defaultColor = tool?.defaultColor ?? "#ffffcc";
    this.el.innerHTML = `
      <label class="opt">Author: <input type="text" value="${author}" class="note-author-input" style="width:100px"></label>
      <label class="opt">Color: <input type="color" value="${defaultColor}" class="note-color-input"></label>
      <button type="button" class="note-clear-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Clear All</button>
    `;
    const authorInput = this.el.querySelector<HTMLInputElement>(".note-author-input");
    authorInput?.addEventListener("change", () => {
      if (this.tool) this.tool.author = authorInput.value;
    });
    const colorInput = this.el.querySelector<HTMLInputElement>(".note-color-input");
    colorInput?.addEventListener("input", () => {
      if (this.tool) this.tool.defaultColor = colorInput.value;
    });
    const clearBtn = this.el.querySelector<HTMLButtonElement>(".note-clear-btn");
    clearBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.clearAll === "function") this.tool.clearAll();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Count Tool Options ──────────────────────────────────────

export class CountToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const count = tool?.count ?? tool?.markers?.length ?? 0;
    this.el.innerHTML = `
      <span class="opt">Count: <span class="count-readout">${count}</span></span>
      <button type="button" class="count-clear-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Clear</button>
    `;
    const clearBtn = this.el.querySelector<HTMLButtonElement>(".count-clear-btn");
    clearBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.clearAll === "function") this.tool.clearAll();
      const readout = this.el.querySelector<HTMLSpanElement>(".count-readout");
      if (readout) readout.textContent = "0";
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Path Selection / Direct Selection Tool Options ──────────
//
// Neither tool currently has any configurable fields (only internal drag/
// hit-test state). `showBoundingBox` is a dynamic UI-only field mirroring
// Photoshop's real "Show Bounding Box" checkbox for both tools — not yet
// consumed by the renderer, which always draws its own selection handles.

export class PathSelectionToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const showBoundingBox = tool?.showBoundingBox === true;
    this.el.innerHTML = `
      <label class="opt"><input type="checkbox" ${showBoundingBox ? "checked" : ""} class="pathsel-showbbox-input"> Show Bounding Box</label>
    `;
    const showBBoxInput = this.el.querySelector<HTMLInputElement>(".pathsel-showbbox-input");
    showBBoxInput?.addEventListener("change", () => {
      if (this.tool) this.tool.showBoundingBox = showBBoxInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

export class DirectSelectionToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const showBoundingBox = tool?.showBoundingBox === true;
    this.el.innerHTML = `
      <label class="opt"><input type="checkbox" ${showBoundingBox ? "checked" : ""} class="directsel-showbbox-input"> Show Bounding Box</label>
    `;
    const showBBoxInput = this.el.querySelector<HTMLInputElement>(".directsel-showbbox-input");
    showBBoxInput?.addEventListener("change", () => {
      if (this.tool) this.tool.showBoundingBox = showBBoxInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Erase to History Tool Options ───────────────────────────

export class EraseToHistoryToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private onChange: () => void;

  constructor(tool: any, onChange: () => void) {
    this.tool = tool;
    this.onChange = onChange;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const size = tool?.size ?? 30;
    const hardness = tool?.hardness ?? 0.5;
    const opacity = tool?.opacity ?? 1;
    this.el.innerHTML = `
      <label class="opt">Size: <input type="range" min="1" max="2000" value="${size}" class="e2h-size-input"> <span class="e2h-size-label">${size}</span></label>
      <label class="opt">Hardness: <input type="range" min="0" max="100" value="${Math.round(hardness * 100)}" class="e2h-hardness-input"> <span class="e2h-hardness-label">${Math.round(hardness * 100)}%</span></label>
      <label class="opt">Opacity: <input type="range" min="0" max="100" value="${Math.round(opacity * 100)}" class="e2h-opacity-input"> <span class="e2h-opacity-label">${Math.round(opacity * 100)}%</span></label>
    `;
    this.bindSlider(".e2h-size-input", ".e2h-size-label", "size", 1, false);
    this.bindSlider(".e2h-hardness-input", ".e2h-hardness-label", "hardness", 0.01, true);
    this.bindSlider(".e2h-opacity-input", ".e2h-opacity-label", "opacity", 0.01, true);
  }

  private bindSlider(inputSel: string, labelSel: string, key: string, scale: number, showPct: boolean): void {
    const input = this.el.querySelector<HTMLInputElement>(inputSel);
    const label = this.el.querySelector<HTMLSpanElement>(labelSel);
    if (!input || !label) return;
    input.addEventListener("input", () => {
      if (this.tool) this.tool[key] = parseFloat(input.value) * scale;
      label.textContent = showPct ? `${input.value}%` : String(input.value);
      this.onChange();
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Perspective Crop Tool Options ───────────────────────────
//
// Mirrors CropToolOptions's Commit/Cancel button pattern — `state` is
// passed through to PerspectiveCropTool.commit()/cancel() the same way
// CropToolOptions already does for CropTool.

export class PerspectiveCropToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;
  private state: any;

  constructor(tool: any, state?: any) {
    this.tool = tool;
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const progress = typeof tool?.getProgress === "function" ? tool.getProgress() : 0;
    this.el.innerHTML = `
      <span class="opt">Corners: <span class="perscrop-progress">${progress}</span>/4</span>
      <button class="perscrop-commit-btn" type="button" title="Commit crop (Enter)" style="margin-left:12px;padding:2px 10px;cursor:pointer;font-weight:bold;">&#10003;</button>
      <button class="perscrop-cancel-btn" type="button" title="Cancel crop (Esc)" style="margin-left:2px;padding:2px 10px;cursor:pointer;">&#10005;</button>
    `;
    const commitBtn = this.el.querySelector<HTMLButtonElement>(".perscrop-commit-btn");
    commitBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.commit === "function") this.tool.commit(this.state);
    });
    const cancelBtn = this.el.querySelector<HTMLButtonElement>(".perscrop-cancel-btn");
    cancelBtn?.addEventListener("click", () => {
      if (this.tool && typeof this.tool.cancel === "function") this.tool.cancel(this.state);
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Frame Tool Options ──────────────────────────────────────

export class FrameToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const shape = tool?.shape ?? "rect";
    this.el.innerHTML = `
      <label class="opt">Shape:
        <select class="frame-shape-input">
          <option value="rect" ${shape === "rect" ? "selected" : ""}>Rectangle</option>
          <option value="ellipse" ${shape === "ellipse" ? "selected" : ""}>Ellipse</option>
        </select>
      </label>
    `;
    const shapeInput = this.el.querySelector<HTMLSelectElement>(".frame-shape-input");
    shapeInput?.addEventListener("change", () => {
      if (this.tool) this.tool.shape = shapeInput.value;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Hand Tool Options ───────────────────────────────────────
//
// HandTool itself has no configurable fields (it just pans the viewport);
// "Scroll All Windows" is a state-level checkbox (mirrors MoveToolOptions's
// state-based pattern above). Pixelpa is single-document, so this is a
// UI-only placeholder matching Photoshop's real Hand tool options bar —
// there's only ever one window to scroll.

export class HandToolOptions implements ToolOptions {
  private el: HTMLElement;
  private state: any;

  constructor(state?: any) {
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const scrollAll = state?.scrollAllWindows === true;
    this.el.innerHTML = `
      <label class="opt"><input type="checkbox" ${scrollAll ? "checked" : ""} class="hand-scrollall-input"> Scroll All Windows</label>
    `;
    const scrollAllInput = this.el.querySelector<HTMLInputElement>(".hand-scrollall-input");
    scrollAllInput?.addEventListener("change", () => {
      if (this.state) this.state.scrollAllWindows = scrollAllInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Zoom Tool Options ───────────────────────────────────────
//
// "Zoom %" is a real, functional control: it reads/writes state.viewport.zoom
// via the same `zoomAt()` helper main.ts's Zoom tool click handler uses,
// anchored at the cursor-canvas center (the closest thing ToolUX has to the
// visible viewport rect). "Resize Windows to Fit" / "Zoom All Windows" are
// state-level UI-only checkboxes — Pixelpa is single-document/single-window,
// so they mirror Photoshop's real Zoom options bar without having an actual
// multi-window effect.

export class ZoomToolOptions implements ToolOptions {
  private el: HTMLElement;
  private state: any;
  private canvas: HTMLCanvasElement | null;

  constructor(state?: any, canvas?: HTMLCanvasElement | null) {
    this.state = state;
    this.canvas = canvas ?? null;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const zoomPct = Math.round((state?.viewport?.zoom ?? 1) * 100);
    const resizeWindows = state?.resizeWindowsToFit === true;
    const zoomAll = state?.zoomAllWindows === true;
    this.el.innerHTML = `
      <label class="opt">Zoom: <input type="number" min="1" max="32000" value="${zoomPct}" class="zoom-pct-input" style="width:60px">%</label>
      <label class="opt"><input type="checkbox" ${resizeWindows ? "checked" : ""} class="zoom-resizewindows-input"> Resize Windows to Fit</label>
      <label class="opt"><input type="checkbox" ${zoomAll ? "checked" : ""} class="zoom-zoomall-input"> Zoom All Windows</label>
    `;
    const zoomInput = this.el.querySelector<HTMLInputElement>(".zoom-pct-input");
    zoomInput?.addEventListener("change", () => {
      if (!this.state?.viewport || typeof this.state.setViewport !== "function") return;
      const pct = Math.max(1, Math.min(32000, parseFloat(zoomInput.value) || 100));
      const factor = (pct / 100) / (this.state.viewport.zoom || 1);
      const w = this.canvas?.clientWidth || 0;
      const h = this.canvas?.clientHeight || 0;
      this.state.setViewport(zoomAt(this.state.viewport, factor, w / 2, h / 2));
    });
    const resizeWindowsInput = this.el.querySelector<HTMLInputElement>(".zoom-resizewindows-input");
    resizeWindowsInput?.addEventListener("change", () => {
      if (this.state) this.state.resizeWindowsToFit = resizeWindowsInput.checked;
    });
    const zoomAllInput = this.el.querySelector<HTMLInputElement>(".zoom-zoomall-input");
    zoomAllInput?.addEventListener("change", () => {
      if (this.state) this.state.zoomAllWindows = zoomAllInput.checked;
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Rotate View Tool Options ────────────────────────────────

export class RotateViewToolOptions implements ToolOptions {
  private el: HTMLElement;
  private state: any;

  constructor(state?: any) {
    this.state = state;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const rotation = state?.viewport?.rotation ?? 0;
    this.el.innerHTML = `
      <label class="opt">Rotation: <input type="number" step="0.1" value="${rotation}" class="rotateview-angle-input" style="width:60px">&deg;</label>
      <button type="button" class="rotateview-reset-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Reset View</button>
    `;
    const angleInput = this.el.querySelector<HTMLInputElement>(".rotateview-angle-input");
    angleInput?.addEventListener("change", () => {
      if (this.state && typeof this.state.setViewport === "function") {
        const v = parseFloat(angleInput.value) || 0;
        this.state.setViewport({ rotation: v });
      }
    });
    const resetBtn = this.el.querySelector<HTMLButtonElement>(".rotateview-reset-btn");
    resetBtn?.addEventListener("click", () => {
      if (this.state && typeof this.state.setViewport === "function") {
        this.state.setViewport({ rotation: 0 });
        if (angleInput) angleInput.value = "0";
      }
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

// ── Slice / Slice Select Tool Options ───────────────────────
//
// `style` is a dynamic UI-only field — SliceTool.onPointerUp() always draws
// a free-form rectangle regardless of this setting (Fixed Aspect Ratio /
// Fixed Size are not yet implemented). "Clear Slices" / "Delete Slice"
// operate on the tool's real `slices` array.

export class SliceToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const style = tool?.style ?? "normal";
    this.el.innerHTML = `
      <label class="opt">Style:
        <select class="slice-style-input">
          <option value="normal" ${style === "normal" ? "selected" : ""}>Normal</option>
          <option value="fixed-ratio" ${style === "fixed-ratio" ? "selected" : ""}>Fixed Aspect Ratio</option>
          <option value="fixed-size" ${style === "fixed-size" ? "selected" : ""}>Fixed Size</option>
        </select>
      </label>
      <span class="opt">Slices: <span class="slice-count">${tool?.slices?.length ?? 0}</span></span>
      <button type="button" class="slice-clear-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Clear Slices</button>
    `;
    const styleInput = this.el.querySelector<HTMLSelectElement>(".slice-style-input");
    styleInput?.addEventListener("change", () => {
      if (this.tool) this.tool.style = styleInput.value;
    });
    const clearBtn = this.el.querySelector<HTMLButtonElement>(".slice-clear-btn");
    clearBtn?.addEventListener("click", () => {
      if (this.tool && Array.isArray(this.tool.slices)) {
        this.tool.slices.length = 0;
        const countEl = this.el.querySelector<HTMLSpanElement>(".slice-count");
        if (countEl) countEl.textContent = "0";
      }
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}

export class SliceSelectToolOptions implements ToolOptions {
  private el: HTMLElement;
  private tool: any;

  constructor(tool: any) {
    this.tool = tool;
    this.el = document.createElement("div");
    this.el.className = "optionsbar__group";
    const selectedId = tool?.selectedSliceId ?? null;
    this.el.innerHTML = `
      <span class="opt">Selected: <span class="sliceselect-selected">${selectedId ?? "None"}</span></span>
      <button type="button" class="sliceselect-delete-btn opt-btn" style="margin-left:8px;padding:2px 10px;cursor:pointer;">Delete Slice</button>
    `;
    const deleteBtn = this.el.querySelector<HTMLButtonElement>(".sliceselect-delete-btn");
    deleteBtn?.addEventListener("click", () => {
      if (!this.tool || !Array.isArray(this.tool.slices) || !this.tool.selectedSliceId) return;
      const idx = this.tool.slices.findIndex((sl: any) => sl.id === this.tool.selectedSliceId);
      if (idx >= 0) this.tool.slices.splice(idx, 1);
      this.tool.selectedSliceId = null;
      const selEl = this.el.querySelector<HTMLSpanElement>(".sliceselect-selected");
      if (selEl) selEl.textContent = "None";
    });
  }

  render(): HTMLElement { return this.el; }
  destroy(): void {}
}
